import React, {useEffect, useMemo, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import {Alert, Button, Spinner, Tab, Tabs} from 'react-bootstrap';
import {withRouter} from 'react-router-dom';
import {connect} from 'react-redux';
import {FaAngleLeft, FaAngleRight, FaChartBar, FaCropAlt, FaDownload, FaExpand, FaFire, FaHome, FaPause, FaPlay, FaPlayCircle, FaRedo, FaSearch, FaShareAlt, FaTrophy, FaUser, FaUsers} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import PlayerVideoAnalysisSharing from './player-video-analysis-sharing';
import * as api from '../api';

const formatValue = (value, suffix = '') => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  return `${value}${suffix}`;
};

const formatDistanceMeters = value => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const meters = Number(value) * 0.3048;
  if (!Number.isFinite(meters)) {
    return '-';
  }
  return `${Math.round(meters)} m`;
};

const formatTime = ms => {
  const seconds = Math.max(0, Math.floor(Number(ms || 0) / 1000));
  return new Date(seconds * 1000).toISOString().slice(11, 19);
};

const teamLabel = (team, t) => {
  if (team === null || team === undefined || team === '') {
    return '-';
  }
  return t('ANALYSIS_TEAM_NUMBER', {number: Number(team) + 1});
};

const playerLabel = (playerId, t) => {
  if (playerId === null || playerId === undefined || playerId === '') {
    return '-';
  }
  return t('ANALYSIS_PLAYER_NUMBER', {number: Number(playerId) + 1});
};

const defaultPlayerName = (player, t) => (
  player && player.defaultName ? player.defaultName : playerLabel(player && player.id, t)
);

const scoreToDraft = score => {
  const normalizeScorePart = value => (value === null || value === undefined || value === '' ? '' : String(value));
  if (Array.isArray(score)) {
    return {
      team1: normalizeScorePart(score[0]),
      team2: normalizeScorePart(score[1])
    };
  }
  if (score && typeof score === 'object') {
    const nestedScore = score.score ||
      score.scores ||
      score.teamScores ||
      score.team_scores ||
      score.game_score ||
      score.runningScore ||
      score.running_score ||
      (score.scoringInfo && (score.scoringInfo.runningScore || score.scoringInfo.running_score || score.scoringInfo.score)) ||
      (score.scoring_info && (score.scoring_info.runningScore || score.scoring_info.running_score || score.scoring_info.score));
    if (nestedScore && nestedScore !== score) {
      return scoreToDraft(nestedScore);
    }
    return {
      team1: normalizeScorePart(score.team1 ?? score.team_1 ?? score.team0 ?? score.team_0 ?? score[0]),
      team2: normalizeScorePart(score.team2 ?? score.team_2 ?? score.team1Score ?? score.team_1_score ?? score[1])
    };
  }
  if (typeof score === 'string') {
    const parts = score.split(/[-:]/).map(part => part.trim()).filter(Boolean);
    if (parts.length >= 2) {
      return {team1: parts[0], team2: parts[1]};
    }
  }
  return {team1: '', team2: ''};
};

const formatSegmentScore = score => {
  const draft = scoreToDraft(score);
  if (!draft.team1 && !draft.team2) {
    return '-';
  }
  return `${draft.team1 || '0'} - ${draft.team2 || '0'}`;
};

const segmentScore = segment => (
  segment
    ? segment.manualScore || segment.score || segment.scoringInfo || segment.scoring_info
    : null
);

const SHOT_SEQUENCE_FILTERS = [
  {id: 'serve', label: 'ANALYSIS_SERVE'},
  {id: 'return', label: 'ANALYSIS_RETURN'},
  {id: 'third_shot', label: 'ANALYSIS_THIRD_SHOT'},
  {id: 'fourth_shot', label: 'ANALYSIS_FOURTH_SHOT'},
  {id: 'fifth_shot', label: 'ANALYSIS_FIFTH_SHOT'},
  {id: 'last_shot', label: 'ANALYSIS_LAST_SHOT'}
];

const SHOT_TYPE_FILTERS = [
  {id: 'dink', label: 'ANALYSIS_DINK'},
  {id: 'drive', label: 'ANALYSIS_DRIVE'},
  {id: 'drop', label: 'ANALYSIS_DROP'},
  {id: 'atp', label: 'ANALYSIS_ATP'},
  {id: 'erne', label: 'ANALYSIS_ERNE'},
  {id: 'lob', label: 'ANALYSIS_LOB'},
  {id: 'reset', label: 'ANALYSIS_RESET'},
  {id: 'smash', label: 'ANALYSIS_SMASH'},
  {id: 'speedup', label: 'ANALYSIS_SPEEDUP'}
];

const SHOT_ERROR_FILTERS = [
  {id: 'error_net', label: 'ANALYSIS_NET'},
  {id: 'error_out', label: 'ANALYSIS_OUT'},
  {id: 'error_unforced', label: 'ANALYSIS_UNFORCED'},
  {id: 'error_intercepted', label: 'ANALYSIS_INTERCEPTED'},
  {id: 'error_popup', label: 'ANALYSIS_POPUP'}
];

const SHOT_EXPLORER_PLAYBACK_SCOPES = [
  {id: 'rally', label: 'ANALYSIS_WHOLE_RALLY'},
  {id: 'shot', label: 'ANALYSIS_SHOT_ONLY'}
];

const DEFAULT_SHOT_QUALITY_FILTER = {min: 0, max: 100};

const GAME_STAT_SHOTS = [
  {key: 'serves', label: 'Serve', filterId: 'serve'},
  {key: 'returns', label: 'Return', filterId: 'return'},
  {key: 'thirds', label: '3rd shot', filterId: 'third_shot'},
  {key: 'drives', label: 'Drive', filterId: 'drive'},
  {key: 'drops', label: 'Drop', filterId: 'drop'},
  {key: 'dinks', label: 'Dink', filterId: 'dink'},
  {key: 'smashes', label: 'Smash', filterId: 'smash'},
  {key: 'third_drives', label: '3rd drive', filterId: 'third_shot_drive'},
  {key: 'third_drops', label: '3rd drop', filterId: 'third_shot'}
];

const normalizedShotSearchText = shot => {
  if (!shot) {
    return '';
  }
  return [
    shot.label,
    shot.shotType,
    shot.strokeType,
    shot.verticalType,
    shot.error,
    shot.outcome,
    shot.result,
    shot.isVolley ? 'volley' : null,
    ...(Array.isArray(shot.tags) ? shot.tags : [])
  ]
    .filter(Boolean)
    .join(' ')
    .replace(/[_;]/g, ' ')
    .toLowerCase();
};

const shotErrorSearchText = shot => (
  normalizedShotSearchText(shot)
    .concat(' ', JSON.stringify((shot || {}).error || ''))
    .toLowerCase()
);

const shotMatchesExplorerFilter = (shot, filterId, rallyShots = []) => {
  const label = normalizedShotSearchText(shot);
  const shotIndex = Number(shot && shot.shotIndex);
  const orderedRallyShots = rallyShots.slice().sort((a, b) => Number(a.shotIndex || 0) - Number(b.shotIndex || 0));
  const lastShot = orderedRallyShots[orderedRallyShots.length - 1] || null;
  const errorLabel = shotErrorSearchText(shot);
  switch (filterId) {
  case 'final_winner':
  case 'final_error':
    return false;
  case 'serve':
    return shotIndex === 0 || label.includes('serve') || label.includes('servizio');
  case 'return':
    return shotIndex === 1 || label.includes('return') || label.includes('risposta');
  case 'third_shot':
    return shotIndex === 2;
  case 'fourth_shot':
    return shotIndex === 3;
  case 'fifth_shot':
    return shotIndex === 4;
  case 'last_shot':
    return lastShot ? shotIdentity(shot) === shotIdentity(lastShot) : false;
  case 'third_shot_drive':
    return shotIndex === 2 && label.includes('drive');
  case 'volley_dink':
    return label.includes('volley') && label.includes('dink');
  case 'volley_drive':
    return label.includes('volley') && label.includes('drive');
  case 'error_net':
    return errorLabel.includes('net');
  case 'error_out':
    return errorLabel.includes('out') || errorLabel.includes('long') || errorLabel.includes('wide');
  case 'error_unforced':
    return errorLabel.includes('unforced');
  case 'error_intercepted':
    return errorLabel.includes('intercept');
  case 'error_popup':
    return errorLabel.includes('popup') || errorLabel.includes('pop up');
  default:
    return label.includes(filterId.replace(/_/g, ' ')) || label.includes(filterId);
  }
};

const shotIdentity = (shot, rallyId = '') => {
  if (!shot) {
    return `${rallyId}-empty`;
  }
  if (shot.id !== null && shot.id !== undefined && shot.id !== '') {
    return `id-${shot.id}`;
  }
  return `${rallyId}-${shot.shotIndex || 0}-${shot.startMs || 0}-${shot.endMs || 0}`;
};

const buildShotExplorerResults = ({rallies, shots, rallyEndEvents, players, types, quality}) => {
  const selectedPlayers = players.map(Number);
  const selectedFinalTypes = types.filter(filterId => filterId === 'final_winner' || filterId === 'final_error');
  const selectedRegularTypes = types.filter(filterId => filterId !== 'final_winner' && filterId !== 'final_error');
  return rallies
    .map(rally => {
      const rallyShots = shots.filter(shot => Number(shot.rallyIndex) === Number(rally.id));
      const matchingRegularShots = rallyShots.filter(shot => {
        const playerMatches = selectedPlayers.length === 0 || selectedPlayers.includes(Number(shot.playerId));
        const shotQuality = Number(shot.quality);
        const qualityMatches = Number.isNaN(shotQuality) ||
          (shotQuality >= Number(quality.min || 0) && shotQuality <= Number(quality.max || 100));
        const typeMatches = selectedRegularTypes.length === 0 && selectedFinalTypes.length === 0
          ? true
          : selectedRegularTypes.some(filterId => shotMatchesExplorerFilter(shot, filterId, rallyShots));
        return playerMatches && qualityMatches && typeMatches;
      });
      const rallyEndEvent = rallyEndEvents.find(event => Number(event.rally.id) === Number(rally.id)) || null;
      const finalTypeMatches = rallyEndEvent && selectedFinalTypes.some(filterId => (
        (filterId === 'final_winner' && rallyEndEvent.resultType === 'winner') ||
        (filterId === 'final_error' && rallyEndEvent.resultType === 'error')
      ));
      const finalPlayerMatches = rallyEndEvent && (selectedPlayers.length === 0 || selectedPlayers.includes(Number(rallyEndEvent.finalPlayerId)));
      const matchingFinalShots = finalTypeMatches && finalPlayerMatches && rallyEndEvent.finalShot ? [rallyEndEvent.finalShot] : [];
      const matchingShots = [...matchingRegularShots, ...matchingFinalShots]
        .filter((shot, index, list) => list.findIndex(item => shotIdentity(item, rally.id) === shotIdentity(shot, rally.id)) === index)
        .sort((a, b) => Number(a.startMs || 0) - Number(b.startMs || 0));
      return {rally, matchingShots};
    })
    .filter(item => item.matchingShots.length > 0);
};

const buildShotExplorerClipSegments = (results, durationMs, t) => results.flatMap(({rally, matchingShots}) => matchingShots.map((shot, index) => {
  const startMs = Number(shot.startMs || rally.startMs || 0);
  const endMs = Number(shot.endMs || startMs);
  return {
    id: `shot-explorer-${rally.id}-${shot.id || index}`,
    segmentType: 'shot-clip',
    title: t('ANALYSIS_RALLY_NUMBER', {number: rally.number}),
    displayTitle: `${translateText(shot.label || 'shot', t)} | ${t('ANALYSIS_RALLY_NUMBER', {number: rally.number})}`,
    startMs: Math.max(0, startMs - 2000),
    endMs: Math.min(durationMs, Math.max(endMs + 2000, startMs + 2500)),
    rally,
    shot,
    number: rally.number
  };
}));

const LABELS = {
  dink_consistency: 'ANALYSIS_DINK_CONSISTENCY', drop_shot_consistency: 'ANALYSIS_DROP_CONSISTENCY', legal_serve_percentage: 'ANALYSIS_LEGAL_SERVES', legal_third_shot_percentage: 'ANALYSIS_LEGAL_THIRD_SHOTS', kitchen_arrival_percentage_on_serve: 'ANALYSIS_KITCHEN_ARRIVAL_SERVE', rallies_won_with_resets: 'ANALYSIS_RALLIES_WON_RESETS', rallies_won_with_speedups: 'ANALYSIS_RALLIES_WON_SPEEDUPS', return_quality: 'ANALYSIS_RETURN_QUALITY', serve_quality: 'ANALYSIS_SERVE_QUALITY', third_shot_quality: 'ANALYSIS_THIRD_SHOT_QUALITY', long_battle: 'ANALYSIS_LONG_EXCHANGE', firefight: 'ANALYSIS_FAST_EXCHANGE', passing: 'ANALYSIS_PASSING', poach: 'ANALYSIS_POACH', sequence: 'ANALYSIS_SEQUENCE', drive: 'ANALYSIS_DRIVE', drop: 'ANALYSIS_DROP', dink: 'ANALYSIS_DINK', smash: 'ANALYSIS_SMASH', overhead: 'ANALYSIS_OVERHEAD', volley: 'ANALYSIS_VOLLEY', return: 'ANALYSIS_RETURN', serve: 'ANALYSIS_SERVE', shot: 'ANALYSIS_SHOT'
};

const translateToken = (value, t) => {
  if (!value) {
    return '-';
  }
  const key = String(value).trim().toLowerCase().replace(/\s+/g, '_');
  if (LABELS[key]) {
    return t(LABELS[key]);
  }
  return key
    .split('_')
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
};

const translateText = (value, t) => {
  if (!value) {
    return '-';
  }
  const key = String(value).trim().toLowerCase().replace(/\s+/g, '_');
  if (LABELS[key]) {
    return t(LABELS[key]);
  }
  return String(value)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, letter => letter.toUpperCase());
};

const statValue = value => {
  if (value === null || value === undefined || value === '') {
    return '-';
  }
  const number = Number(value);
  if (Number.isNaN(number)) {
    return value;
  }
  return Number.isInteger(number) ? String(number) : String(Math.round(number * 10) / 10);
};

const statPercent = value => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return '-';
  }
  return `${Math.round(number * 10) / 10}%`;
};

const qualityPercent = value => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return '-';
  }
  return statPercent(number <= 1 ? number * 100 : number);
};

const clampScore = value => Math.max(0, Math.min(100, Number(value || 0)));

const readableZoneInsight = zone => {
  if (!zone) {
    return '';
  }
  const team1 = Number((zone.teamValues[0] || {}).count || 0);
  const team2 = Number((zone.teamValues[1] || {}).count || 0);
  const leader = team1 === team2 ? 'Entrambi i team' : team1 > team2 ? 'Team 1' : 'Team 2';
  return `${leader}: piu colpi in questa zona. Successo medio ${statPercent(zone.avgSuccess)}.`;
};

const shotSuccessValue = shot => (((shot || {}).outcome_stats || {}).success_percentage);

const firstAvailableNumber = (source, keys) => {
  if (!source) {
    return null;
  }
  for (const key of keys) {
    const value = source[key];
    const number = Number(value);
    if (value !== null && value !== undefined && value !== '' && !Number.isNaN(number)) {
      return number;
    }
  }
  return null;
};

const shotSpeedValue = (shot, speedKeys) => {
  const nestedValue = firstAvailableNumber(shot && shot.speed_stats, speedKeys);
  if (nestedValue !== null) {
    return nestedValue;
  }
  return firstAvailableNumber(shot, speedKeys);
};

const formatSpeed = value => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return '-';
  }
  return `${Math.round(number * 10) / 10} mph`;
};

const normalizedScore = value => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return null;
  }
  return clampScore(number <= 1 ? number * 100 : number);
};

const averageScore = values => {
  const numbers = values
    .map(normalizedScore)
    .filter(value => value !== null && value !== undefined && !Number.isNaN(value));
  if (numbers.length === 0) {
    return null;
  }
  return numbers.reduce((sum, value) => sum + value, 0) / numbers.length;
};

const duprRating = value => {
  const number = Number(value);
  if (Number.isNaN(number)) {
    return null;
  }
  if (number >= 2 && number <= 8) {
    return number;
  }
  const normalized = number <= 1 ? number * 100 : number;
  return 2 + (clampScore(normalized) / 100) * 6;
};

const duprRatingPercent = value => {
  const rating = duprRating(value);
  if (rating === null) {
    return 0;
  }
  return Math.max(0, Math.min(100, ((rating - 2) / 6) * 100));
};

const formatRating = value => {
  const rating = duprRating(value);
  if (rating === null) {
    return '-';
  }
  return rating.toFixed(2).replace(/0$/, '').replace(/\.0$/, '');
};

const playerTeamIndex = player => {
  if (!player) {
    return null;
  }
  if (player.team !== null && player.team !== undefined && player.team !== '') {
    const team = Number(player.team);
    return Number.isNaN(team) ? null : team;
  }
  if (player.teamNumber !== null && player.teamNumber !== undefined && player.teamNumber !== '') {
    const teamNumber = Number(player.teamNumber);
    return Number.isNaN(teamNumber) ? null : teamNumber - 1;
  }
  return null;
};

const hasFinalShotError = error => {
  if (!error) {
    return false;
  }
  if (typeof error === 'string') {
    const value = error.toLowerCase();
    return value.includes('fault') || value.includes('net') || value.includes('out');
  }
  const serialized = JSON.stringify(error).toLowerCase();
  return serialized.includes('true') || serialized.includes('out') || serialized.includes('net') || serialized.includes('fault');
};

const metricCards = (overview, t) => [
  {label: t('ANALYSIS_SCORE'), value: Array.isArray(overview.score) ? overview.score.join(' - ') : overview.score},
  {label: t('ANALYSIS_RALLIES'), value: overview.totalRallies},
  {label: t('ANALYSIS_TOTAL_SHOTS'), value: overview.totalShots},
  {label: t('ANALYSIS_AVERAGE_SHOTS_RALLY'), value: overview.averageShotsPerRally},
  {label: t('ANALYSIS_KITCHEN_RALLIES'), value: overview.kitchenRallies},
  {label: t('ANALYSIS_LONGEST_RALLY'), value: overview.longestRallyShots}
];

const SEGMENT_INTRO_MS = 800;
const SEGMENT_INTRO_SEEK_DELAY_MS = 180;
const DEFAULT_ANALYSIS_PLAYBACK_MODE = 'rallies';

const PlayerVideoAnalysisPage = ({match, history, token, profile}) => {
  const {t} = useTranslation();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentMs, setCurrentMs] = useState(0);
  const [activeSegment, setActiveSegment] = useState(null);
  const [segmentIntro, setSegmentIntro] = useState(null);
  const [playbackMode, setPlaybackMode] = useState(DEFAULT_ANALYSIS_PLAYBACK_MODE);
  const [activePlaylistIndex, setActivePlaylistIndex] = useState(0);
  const [scoreDraft, setScoreDraft] = useState({team1: '', team2: ''});
  const [savingScore, setSavingScore] = useState(false);
  const [scoreSaveError, setScoreSaveError] = useState(null);
  const [playerNames, setPlayerNames] = useState({});
  const [savingPlayers, setSavingPlayers] = useState(false);
  const [playerSaveError, setPlayerSaveError] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [analysisView, setAnalysisView] = useState('home');
  const [selectedPlayerFocusId, setSelectedPlayerFocusId] = useState(null);
  const [shotExplorerPlayers, setShotExplorerPlayers] = useState([]);
  const [shotExplorerTypes, setShotExplorerTypes] = useState([]);
  const [shotExplorerPlaybackScope, setShotExplorerPlaybackScope] = useState('rally');
  const [shotExplorerQuality, setShotExplorerQuality] = useState(DEFAULT_SHOT_QUALITY_FILTER);
  const [shotFilterDraftPlayers, setShotFilterDraftPlayers] = useState([]);
  const [shotFilterDraftTypes, setShotFilterDraftTypes] = useState([]);
  const [shotFilterDraftQuality, setShotFilterDraftQuality] = useState(DEFAULT_SHOT_QUALITY_FILTER);
  const [shotFilterDraftPlaybackScope, setShotFilterDraftPlaybackScope] = useState('shot');
  const [isShotFilterPanelOpen, setShotFilterPanelOpen] = useState(false);
  const [shotPerformanceFilter, setShotPerformanceFilter] = useState('all');
  const [shotClipSegments, setShotClipSegments] = useState([]);
  const [isAnalysisSharingModalVisible, setAnalysisSharingModalVisible] = useState(false);
  const [analysisSharingTarget, setAnalysisSharingTarget] = useState(null);
  const [downloadSelectionMode, setDownloadSelectionMode] = useState(false);
  const [downloadSelectedKeys, setDownloadSelectedKeys] = useState([]);
  const [downloadPreparing, setDownloadPreparing] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [downloadResult, setDownloadResult] = useState(null);
  const [pendingDeepLink, setPendingDeepLink] = useState(null);
  const [isSimulatedFullscreen, setSimulatedFullscreen] = useState(false);
  const videoRef = useRef(null);
  const segmentIntroTimerRef = useRef(null);
  const segmentIntroHideTimerRef = useRef(null);
  const initialAutoplayRef = useRef(false);
  const sidePlaylistRef = useRef(null);
  const downloadPollTimerRef = useRef(null);
  const analysisRoute = useMemo(() => ({
    requestId: match && match.params ? match.params.requestId : null,
    sharingId: match && match.params ? match.params.sharingId : null
  }), [match]);
  const isSharedAnalysis = !!analysisRoute.sharingId;
  const isPartnerAnalysis = !!(profile && profile.user && profile.user.role === 1);

  useEffect(() => {
    const requestId = analysisRoute.requestId;
    const sharingId = analysisRoute.sharingId;
    if (!requestId && !sharingId) {
      return;
    }
    if (requestId && !token) {
      return;
    }

    setLoading(true);
    setError(null);
    const reportRequest = sharingId
      ? api.getSharedVideoAnalysisReport(sharingId, token)
      : api.getVideoAnalysisReport(requestId, token);
    reportRequest.then(result => {
      setLoading(false);
      if (result.status >= 200 && result.status < 300) {
        const reportRallies = result.json.rallies || [];
        const initialRally = reportRallies[0] || null;
        const initialPlaybackMode = initialRally ? DEFAULT_ANALYSIS_PLAYBACK_MODE : 'full';
        setReport(result.json);
        const nextNames = {};
        (result.json.players || []).forEach(player => {
          nextNames[player.id] = player.name || defaultPlayerName(player, t);
        });
        setPlayerNames(nextNames);
        setCurrentMs(Number((initialRally && initialRally.startMs) || 0));
        setActiveSegment(initialRally);
        setSegmentIntro(null);
        setPlaybackMode(initialPlaybackMode);
        setActivePlaylistIndex(0);
        setScoreDraft(initialRally ? scoreToDraft(segmentScore(initialRally)) : {team1: '', team2: ''});
        setScoreSaveError(null);
        setPlayerSaveError(null);
        setIsVideoPlaying(false);
        setPlaybackRate(1);
        setAnalysisView('home');
        setShotExplorerPlayers([]);
        setShotExplorerTypes([]);
        setShotExplorerPlaybackScope('rally');
        setShotExplorerQuality(DEFAULT_SHOT_QUALITY_FILTER);
        setShotFilterDraftPlayers([]);
        setShotFilterDraftTypes([]);
        setShotFilterDraftQuality(DEFAULT_SHOT_QUALITY_FILTER);
        setShotFilterDraftPlaybackScope('shot');
        setShotFilterPanelOpen(false);
        setDownloadSelectionMode(false);
        setDownloadSelectedKeys([]);
        setDownloadPreparing(false);
        setDownloadError(null);
        setDownloadResult(null);
        initialAutoplayRef.current = false;
        const params = new URLSearchParams(window.location.search || '');
        const mode = params.get('mode');
        if (mode) {
          setPendingDeepLink({
            mode,
            segment: params.get('segment'),
            startMs: params.get('startMs'),
            endMs: params.get('endMs'),
            title: params.get('title'),
            players: params.get('players'),
            types: params.get('types'),
            qmin: params.get('qmin'),
            qmax: params.get('qmax'),
            scope: params.get('scope'),
            playerId: params.get('playerId'),
            resultType: params.get('resultType')
          });
        } else {
          setPendingDeepLink(null);
        }
        return;
      }
      setReport(null);
      setError(t('ANALYSIS_REPORT_LOAD_ERROR'));
    });
  }, [analysisRoute.requestId, analysisRoute.sharingId, t, token]);

  useEffect(() => () => {
    if (segmentIntroTimerRef.current) {
      clearTimeout(segmentIntroTimerRef.current);
    }
    if (segmentIntroHideTimerRef.current) {
      clearTimeout(segmentIntroHideTimerRef.current);
    }
    if (downloadPollTimerRef.current) {
      clearTimeout(downloadPollTimerRef.current);
    }
  }, []);

  useEffect(() => {
    document.body.classList.toggle('analysis-fullscreen-locked', isSimulatedFullscreen);
    const closeOnEscape = event => {
      if (event.key === 'Escape') {
        setSimulatedFullscreen(false);
      }
    };
    if (isSimulatedFullscreen) {
      document.addEventListener('keydown', closeOnEscape);
    }
    return () => {
      document.body.classList.remove('analysis-fullscreen-locked');
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [isSimulatedFullscreen]);

  useEffect(() => {
    if (!pendingDeepLink || !report) {
      return;
    }
    const reportRallies = Array.isArray(report.rallies) ? report.rallies : [];
    const reportHighlights = Array.isArray(report.highlights) ? report.highlights : [];
    if (pendingDeepLink.mode === 'rallies') {
      const segment = reportRallies.find(rally => String(rally.id) === String(pendingDeepLink.segment) || String(rally.number) === String(pendingDeepLink.segment));
      if (segment) {
        const index = reportRallies.findIndex(rally => Number(rally.id) === Number(segment.id));
        setAnalysisView('home');
        setPendingDeepLink(null);
        playSegmentWithIntro(segment, 'rallies', index >= 0 ? index : 0);
      }
      return;
    }
    if (pendingDeepLink.mode === 'highlights') {
      const segment = reportHighlights.find(highlight => String(highlight.id) === String(pendingDeepLink.segment) || String(highlight.number) === String(pendingDeepLink.segment));
      if (segment) {
        const index = reportHighlights.findIndex(highlight => Number(highlight.id) === Number(segment.id));
        setAnalysisView('home');
        setPendingDeepLink(null);
        playSegmentWithIntro(segment, 'highlights', index >= 0 ? index : 0);
      }
      return;
    }
    if (pendingDeepLink.mode === 'shot') {
      const startMs = Number(pendingDeepLink.startMs);
      const endMs = Number(pendingDeepLink.endMs);
      if (!Number.isNaN(startMs) && !Number.isNaN(endMs) && endMs > startMs) {
        const segment = {
          id: `shared-shot-${startMs}-${endMs}`,
          segmentType: 'shot-clip',
          title: pendingDeepLink.title || 'Colpo condiviso',
          displayTitle: pendingDeepLink.title || 'Colpo condiviso',
          startMs,
          endMs,
          number: ''
        };
        setShotClipSegments([segment]);
        setAnalysisView('shot-explorer');
        setShotExplorerPlaybackScope('shot');
        setPendingDeepLink(null);
        playSegmentWithIntro(segment, 'shot-clips', 0);
      }
      return;
    }
    if (pendingDeepLink.mode === 'rally-end-list') {
      const playerId = Number(pendingDeepLink.playerId);
      const resultType = pendingDeepLink.resultType === 'winner' ? 'winner' : 'error';
      if (!Number.isNaN(playerId)) {
        const segments = buildRallyEndClipSegments(playerId, resultType);
        setShotClipSegments(segments);
        setAnalysisView('leaderboards');
        setPlaybackMode('shot-clips');
        setPendingDeepLink(null);
        if (segments.length > 0) {
          playSegmentWithIntro(segments[0], 'shot-clips', 0);
        }
      }
      return;
    }
    if (pendingDeepLink.mode === 'shot-list') {
      const reportShots = Array.isArray(report.shots) ? report.shots : [];
      const reportPlayers = Array.isArray(report.players) ? report.players : [];
      const nextPlayers = (pendingDeepLink.players || '').split(',').filter(Boolean).map(Number).filter(value => !Number.isNaN(value));
      const nextTypes = (pendingDeepLink.types || '').split(',').filter(Boolean);
      const nextQuality = {
        min: pendingDeepLink.qmin !== null && pendingDeepLink.qmin !== undefined && pendingDeepLink.qmin !== '' ? Number(pendingDeepLink.qmin) : DEFAULT_SHOT_QUALITY_FILTER.min,
        max: pendingDeepLink.qmax !== null && pendingDeepLink.qmax !== undefined && pendingDeepLink.qmax !== '' ? Number(pendingDeepLink.qmax) : DEFAULT_SHOT_QUALITY_FILTER.max
      };
      const nextScope = pendingDeepLink.scope === 'rally' ? 'rally' : 'shot';
      const reportDurationMs = Math.max(
        reportRallies.reduce((max, rally) => Math.max(max, Number(rally.endMs || 0)), 0),
        report && report.video ? Number(report.video.durationSeconds || 0) * 1000 : 0,
        1
      );
      const nextRallyEndEvents = reportRallies.map(rally => {
        const finalPlayer = reportPlayers.find(player => Number(player.id) === Number(rally.finalPlayerId));
        const finalTeam = playerTeamIndex(finalPlayer);
        const winningTeam = rally.winningTeam !== null && rally.winningTeam !== undefined && rally.winningTeam !== ''
          ? Number(rally.winningTeam)
          : null;
        const finalShot = reportShots
          .filter(shot => Number(shot.rallyIndex) === Number(rally.id))
          .sort((a, b) => Number(b.shotIndex || 0) - Number(a.shotIndex || 0))[0] || null;
        const isClassified = finalPlayer && finalTeam !== null && winningTeam !== null && !Number.isNaN(winningTeam);
        const resultType = isClassified
          ? (Number(finalTeam) === Number(winningTeam) ? 'winner' : 'error')
          : rally.winnerType || (finalShot && finalShot.winnerType)
            ? 'winner'
            : hasFinalShotError(rally.error || (finalShot && finalShot.error))
              ? 'error'
              : null;
        return {
          rally,
          finalShot,
          finalPlayerId: finalPlayer ? finalPlayer.id : null,
          resultType
        };
      });
      const nextResults = buildShotExplorerResults({
        rallies: reportRallies,
        shots: reportShots,
        rallyEndEvents: nextRallyEndEvents,
        players: nextPlayers,
        types: nextTypes,
        quality: nextQuality
      });
      const nextSegments = nextScope === 'shot'
        ? buildShotExplorerClipSegments(nextResults, reportDurationMs, t)
        : nextResults.map(item => item.rally);
      setShotExplorerPlayers(nextPlayers);
      setShotExplorerTypes(nextTypes);
      setShotExplorerQuality(nextQuality);
      setShotFilterDraftPlayers(nextPlayers);
      setShotFilterDraftTypes(nextTypes);
      setShotFilterDraftQuality(nextQuality);
      setShotFilterDraftPlaybackScope(nextScope);
      setShotExplorerPlaybackScope(nextScope);
      setAnalysisView('shot-explorer');
      setPlaybackMode('shot-explorer');
      setPendingDeepLink(null);
      if (nextSegments.length > 0) {
        playSegmentWithIntro(nextSegments[0], 'shot-explorer', 0);
      }
    }
    // playSegmentWithIntro intentionally omitted: this effect runs only when a URL deep link is loaded.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDeepLink, report]);

  useEffect(() => {
    if (!report) {
      return;
    }
    const list = sidePlaylistRef.current;
    if (!list) {
      return;
    }
    const activeItem = list.querySelector('[data-analysis-active-card="true"]');
    if (!activeItem) {
      return;
    }
    const listRect = list.getBoundingClientRect();
    const itemRect = activeItem.getBoundingClientRect();
    const targetTop = list.scrollTop + (itemRect.top - listRect.top) - (list.clientHeight / 2) + (activeItem.offsetHeight / 2);
    if (Math.abs(list.scrollTop - targetTop) < 12) {
      return;
    }
    list.scrollTo({
      top: Math.max(0, targetTop),
      behavior: 'smooth'
    });
  }, [analysisView, playbackMode, currentMs, activeSegment, report]);

  const analysisRequestId = analysisRoute.requestId;

  const overview = report && report.overview ? report.overview : {};
  const players = report && Array.isArray(report.players) ? report.players : [];
  const rallies = report && Array.isArray(report.rallies) ? report.rallies : [];
  const highlights = report && Array.isArray(report.highlights) ? report.highlights : [];
  const orderedHighlights = (() => {
    const counters = {};
    return highlights
      .slice()
      .sort((a, b) => Number(a.startMs || 0) - Number(b.startMs || 0))
      .map(highlight => {
        const baseTitle = translateText(highlight.title || highlight.kind || 'Highlight', t);
        counters[baseTitle] = (counters[baseTitle] || 0) + 1;
        return {
          ...highlight,
          displayTitle: `${baseTitle} #${counters[baseTitle]}`
        };
      });
  })();
  const shots = report && Array.isArray(report.shots) ? report.shots : [];
  const gameStats = report && report.gameStats ? report.gameStats : {};
  const statsGame = gameStats.game || {};
  const statsPlayers = Array.isArray(gameStats.players) ? gameStats.players : [];
  const videoUrl = report && report.video ? report.video.url : null;
  const durationMs = (() => {
    const lastRallyEnd = rallies.reduce((max, rally) => Math.max(max, Number(rally.endMs || 0)), 0);
    const reportDuration = report && report.video ? Number(report.video.durationSeconds || 0) * 1000 : 0;
    return Math.max(lastRallyEnd, reportDuration, 1);
  })();
  const activeRally = rallies.find(rally => currentMs >= Number(rally.startMs || 0) && currentMs <= Number(rally.endMs || 0));
  const timelineRally = activeRally || rallies.slice().reverse().find(rally => currentMs >= Number(rally.startMs || 0)) || null;
  const activeRallyIndex = activeRally ? rallies.findIndex(rally => rally.id === activeRally.id) : -1;
  const selectedSegment = activeSegment || activeRally;
  const technicalSegment = selectedSegment || null;
  const technicalStartMs = technicalSegment ? Number(technicalSegment.startMs || 0) : Math.max(0, currentMs - 10000);
  const technicalEndMs = technicalSegment ? Number(technicalSegment.endMs || technicalStartMs + 1) : Math.min(durationMs, currentMs + 1);
  const technicalDurationMs = Math.max(1, technicalEndMs - technicalStartMs);
  const technicalProgress = Math.max(0, Math.min(100, ((currentMs - technicalStartMs) / technicalDurationMs) * 100));
  const activeHighlight = orderedHighlights.find(highlight => currentMs >= Number(highlight.startMs || 0) && currentMs <= Number(highlight.endMs || 0));
  const activePlaylistHighlight = activeHighlight || (
    playbackMode === 'highlights' && activeSegment
      ? orderedHighlights.find(highlight => Number(highlight.id) === Number(activeSegment.id))
      : null
  );
  const activePlaylistMode = playbackMode === 'full'
    ? 'full'
    : playbackMode === 'highlights'
      ? 'highlights'
      : playbackMode === 'shot-explorer'
        ? 'shots'
        : playbackMode === 'shot-clips'
          ? 'shot-clips'
          : 'rallies';
  const selectedRallyNumber = selectedSegment && selectedSegment.number
    ? selectedSegment.number
    : activeRally && activeRally.number
      ? activeRally.number
      : rallies.length > 0
        ? rallies[0].number
        : '-';
  const selectedRallyShots = activeRally
    ? shots.filter(shot => Number(shot.rallyIndex) === Number(activeRally.id))
    : [];
  const rallyEndEvents = rallies.map(rally => {
    const finalPlayer = players.find(player => Number(player.id) === Number(rally.finalPlayerId));
    const finalTeam = playerTeamIndex(finalPlayer);
    const winningTeam = rally.winningTeam !== null && rally.winningTeam !== undefined && rally.winningTeam !== ''
      ? Number(rally.winningTeam)
      : null;
    const finalShot = shots
      .filter(shot => Number(shot.rallyIndex) === Number(rally.id))
      .sort((a, b) => Number(b.shotIndex || 0) - Number(a.shotIndex || 0))[0] || null;
    const isClassified = finalPlayer && finalTeam !== null && winningTeam !== null && !Number.isNaN(winningTeam);
    const resultType = isClassified
      ? (Number(finalTeam) === Number(winningTeam) ? 'winner' : 'error')
      : rally.winnerType || (finalShot && finalShot.winnerType)
        ? 'winner'
        : hasFinalShotError(rally.error || (finalShot && finalShot.error))
          ? 'error'
          : null;
    return {
      rally,
      finalShot,
      finalPlayerId: finalPlayer ? finalPlayer.id : null,
      resultType
    };
  });
  const shotExplorerResults = buildShotExplorerResults({
    rallies,
    shots,
    rallyEndEvents,
    players: shotExplorerPlayers,
    types: shotExplorerTypes,
    quality: shotExplorerQuality
  });
  const shotExplorerClipSegments = buildShotExplorerClipSegments(shotExplorerResults, durationMs, t);
  const shotFilterDraftResults = buildShotExplorerResults({
    rallies,
    shots,
    rallyEndEvents,
    players: shotFilterDraftPlayers,
    types: shotFilterDraftTypes,
    quality: shotFilterDraftQuality
  });
  const shotFilterDraftClipCount = buildShotExplorerClipSegments(shotFilterDraftResults, durationMs, t).length;
  const hasAppliedShotFilters = shotExplorerPlayers.length > 0 ||
    shotExplorerTypes.length > 0 ||
    Number(shotExplorerQuality.min) !== Number(DEFAULT_SHOT_QUALITY_FILTER.min) ||
    Number(shotExplorerQuality.max) !== Number(DEFAULT_SHOT_QUALITY_FILTER.max);
  const countShotMatchesForFilters = (filterPlayers, filterTypes, filterQuality) => (
    buildShotExplorerResults({
      rallies,
      shots,
      rallyEndEvents,
      players: filterPlayers,
      types: filterTypes,
      quality: filterQuality
    }).reduce((sum, item) => sum + item.matchingShots.length, 0)
  );
  const draftPlayerFilterCount = playerId => countShotMatchesForFilters([playerId], shotFilterDraftTypes, shotFilterDraftQuality);
  const draftTypeFilterCount = filterId => countShotMatchesForFilters(shotFilterDraftPlayers, [filterId], shotFilterDraftQuality);
  const appliedPlayerFilterCount = playerId => countShotMatchesForFilters([playerId], shotExplorerTypes, shotExplorerQuality);
  const appliedTypeFilterCount = filterId => countShotMatchesForFilters(shotExplorerPlayers, [filterId], shotExplorerQuality);
  const sidePlaylistTitle = playbackMode === 'shot-clips' && shotClipSegments.length > 0
    ? (shotClipSegments[0].resultType === 'winner' ? t('ANALYSIS_WINNERS_PLAYER', {player: shotClipSegments[0].playerName || t('ANALYSIS_PLAYER')}) : t('ANALYSIS_FINAL_ERRORS_PLAYER', {player: shotClipSegments[0].playerName || t('ANALYSIS_PLAYER')}))
    : analysisView === 'shot-explorer'
    ? (shotExplorerPlaybackScope === 'shot' ? (hasAppliedShotFilters ? t('ANALYSIS_FILTERED_SHOTS') : t('ANALYSIS_SHOTS')) : (hasAppliedShotFilters ? t('ANALYSIS_FILTERED_RALLIES') : t('ANALYSIS_RALLIES')))
    : activePlaylistMode === 'full'
      ? t('ANALYSIS_FULL_MATCH')
      : activePlaylistMode === 'highlights'
        ? 'AI Highlights'
        : t('ANALYSIS_RALLIES');
  const sidePlaylistMeta = playbackMode === 'shot-clips' && shotClipSegments.length > 0
    ? t('ANALYSIS_CLIPS_CONTEXT', {count: shotClipSegments.length})
    : analysisView === 'shot-explorer'
    ? (shotExplorerPlaybackScope === 'shot'
      ? t('ANALYSIS_SHOTS_FOUND_ONLY', {count: shotExplorerClipSegments.length})
      : t('ANALYSIS_RALLIES_FOUND_WHOLE', {count: shotExplorerResults.length}))
    : activePlaylistMode === 'full'
      ? t('ANALYSIS_FULL_VIDEO_DURATION', {duration: formatTime(durationMs)})
      : activePlaylistMode === 'highlights'
        ? t('ANALYSIS_HIGHLIGHTS_AVAILABLE', {count: orderedHighlights.length})
        : timelineRally
          ? t('ANALYSIS_RALLY_OF_TOTAL', {number: timelineRally.number, total: rallies.length})
          : t('ANALYSIS_RALLIES_AVAILABLE', {count: rallies.length});
  const shotExplorerPlaylist = shotExplorerPlaybackScope === 'shot'
    ? shotExplorerClipSegments
    : shotExplorerResults.map(item => item.rally);
  const sidePlaylistShareDisabled = !report || !token || (
    activePlaylistMode === 'rallies'
      ? rallies.length === 0
      : activePlaylistMode === 'highlights'
        ? orderedHighlights.length === 0
        : activePlaylistMode === 'shots'
          ? shotExplorerPlaylist.length === 0
          : activePlaylistMode === 'shot-clips'
            ? shotClipSegments.length === 0
          : false
  );
  const playlistSegments = playbackMode === 'highlights'
    ? orderedHighlights
    : playbackMode === 'shot-clips'
      ? shotClipSegments
    : playbackMode === 'shot-explorer'
      ? shotExplorerPlaylist
      : rallies;
  const analysisDownloadRequestId = analysisRoute.requestId || (report && (report.id || report.requestId || report.request_id));
  const downloadPlaylistSegments = activePlaylistMode === 'full'
    ? [{
      id: 'full',
      segmentType: 'full',
      displayTitle: t('ANALYSIS_FULL_MATCH'),
      title: t('ANALYSIS_FULL_MATCH'),
      startMs: 0,
      endMs: durationMs
    }]
    : playlistSegments;
  const downloadSegmentKey = (segment, index) => `${activePlaylistMode}:${segment.segmentType || 'segment'}:${segment.id || `${segment.startMs}-${segment.endMs}`}:${index}`;
  const downloadSegmentTitle = segment => (
    segment.displayTitle || segment.title || (segment.number ? t('ANALYSIS_RALLY_NUMBER', {number: segment.number}) : t('ANALYSIS_CLIP'))
  );
  const downloadItems = downloadPlaylistSegments.map((segment, index) => ({
    key: downloadSegmentKey(segment, index),
    id: segment.id,
    title: downloadSegmentTitle(segment),
    startMs: Math.max(0, Number(segment.startMs || 0)),
    endMs: Math.max(Number(segment.startMs || 0), Number(segment.endMs || segment.startMs || 0)),
    segmentType: segment.segmentType || activePlaylistMode,
    sourceIndex: index,
    rallyId: segment.rally && segment.rally.id ? segment.rally.id : segment.rallyId,
    highlightId: segment.segmentType === 'highlight' ? segment.id : null
  })).filter(item => item.endMs > item.startMs);
  const selectedDownloadItems = downloadItems.filter(item => downloadSelectedKeys.includes(item.key));
  const selectedDownloadDurationMs = selectedDownloadItems.reduce((sum, item) => sum + Math.max(0, item.endMs - item.startMs), 0);
  const allDownloadItemsSelected = downloadItems.length > 0 && selectedDownloadItems.length === downloadItems.length;
  const sidePlaylistDownloadDisabled = !analysisDownloadRequestId || !token || downloadItems.length === 0 || downloadPreparing;

  const displayPlayerName = playerId => {
    const player = players.find(item => Number(item.id) === Number(playerId));
    if (!player) {
      return playerLabel(playerId, t);
    }
    return playerNames[player.id] || player.name || defaultPlayerName(player, t);
  };
  const buildRallyEndClipSegments = (playerId, resultType) => {
    const playerName = displayPlayerName(playerId);
    return rallyEndEvents
      .filter(item => item.resultType === resultType && Number(item.finalPlayerId) === Number(playerId))
      .sort((a, b) => Number((a.finalShot || {}).startMs || a.rally.startMs || 0) - Number((b.finalShot || {}).startMs || b.rally.startMs || 0))
      .map((item, index) => {
        const finalShot = item.finalShot || {};
        const startMs = Number(finalShot.startMs || item.rally.startMs || 0);
        const endMs = Number(finalShot.endMs || item.rally.endMs || startMs);
        return {
          id: `${resultType}-${playerId}-${item.rally.id || index}`,
          segmentType: 'shot-clip',
          title: t(resultType === 'winner' ? 'ANALYSIS_WINNER_NUMBER' : 'ANALYSIS_FINAL_ERROR_NUMBER', {number: index + 1}),
          displayTitle: t(resultType === 'winner' ? 'ANALYSIS_WINNER_NUMBER' : 'ANALYSIS_FINAL_ERROR_NUMBER', {number: index + 1}),
          startMs: Math.max(0, startMs - 2000),
          endMs: Math.min(durationMs, Math.max(endMs + 2000, startMs + 2500)),
          rally: item.rally,
          shot: finalShot,
          resultType,
          playerId,
          playerName,
          number: index + 1
        };
      });
  };
  const playersByTeam = (() => {
    const grouped = {};
    players.forEach(player => {
      const key = player.teamNumber || Number(player.team) + 1 || 0;
      if (!grouped[key]) {
        grouped[key] = [];
      }
      grouped[key].push(player);
    });
    return Object.keys(grouped)
      .sort((a, b) => Number(a) - Number(b))
      .map(teamNumber => ({
        teamNumber,
        players: grouped[teamNumber].sort((a, b) => Number(a.teamPlayerNumber || 0) - Number(b.teamPlayerNumber || 0))
      }));
  })();
  const statsPlayersByTeam = [0, 1].map(teamNumber => {
    const teamPlayers = statsPlayers.filter(player => Number(player.team) === teamNumber);
    const shotCount = teamPlayers.reduce((sum, player) => sum + Number(player.shot_count || 0), 0);
    const distance = teamPlayers.reduce((sum, player) => sum + Number(player.total_distance_covered || 0), 0);
    const avgQuality = teamPlayers.length > 0
      ? teamPlayers.reduce((sum, player) => sum + Number(player.average_shot_quality || 0), 0) / teamPlayers.length
      : null;
    const netFault = teamPlayers.length > 0
      ? teamPlayers.reduce((sum, player) => sum + Number(player.net_fault_percentage || 0), 0) / teamPlayers.length
      : null;
    const outFault = teamPlayers.length > 0
      ? teamPlayers.reduce((sum, player) => sum + Number(player.out_fault_percentage || 0), 0) / teamPlayers.length
      : null;
    return {
      teamNumber: teamNumber + 1,
      shotCount,
      distance,
      avgQuality,
      netFault,
      outFault,
      kitchen: Array.isArray(statsGame.team_percentage_to_kitchen) ? statsGame.team_percentage_to_kitchen[teamNumber] : null,
      shotShare: teamPlayers[0] ? teamPlayers[0].team_shot_percentage : null,
      shortRallies: teamPlayers[0] ? teamPlayers[0].team_short_length_rallies_won : null,
      mediumRallies: teamPlayers[0] ? teamPlayers[0].team_medium_length_rallies_won : null,
      longRallies: teamPlayers[0] ? teamPlayers[0].team_long_length_rallies_won : null
    };
  });
  const playerStatCards = players.map(player => {
    const stats = statsPlayers[player.id] || {};
    const shotProfile = GAME_STAT_SHOTS.map(shot => ({
      ...shot,
      count: Number(((stats[shot.key] || {}).count) || 0),
      quality: (stats[shot.key] || {}).average_quality,
      success: shotSuccessValue(stats[shot.key]),
      rallyWon: (((stats[shot.key] || {}).outcome_stats || {}).rally_won_percentage),
      maxSpeed: shotSpeedValue(stats[shot.key], ['fastest_speed', 'max_speed', 'maximum_speed', 'top_speed', 'fastest_mph', 'max_mph']),
      avgSpeed: shotSpeedValue(stats[shot.key], ['average_speed', 'avg_speed', 'mean_speed', 'average_mph', 'avg_mph'])
    }));
    const mostUsedShot = shotProfile.slice().sort((a, b) => Number(b.count || 0) - Number(a.count || 0))[0];
    const bestShot = shotProfile
      .filter(shot => shot.count > 0)
      .sort((a, b) => Number(b.quality || 0) - Number(a.quality || 0))[0];
    const shotSuccess = key => (((stats[key] || {}).outcome_stats || {}).success_percentage);
    const shotQuality = key => (stats[key] || {}).average_quality;
    const serveScore = averageScore([shotQuality('serves'), shotSuccess('serves')]);
    const returnScore = averageScore([shotQuality('returns'), shotSuccess('returns')]);
    const attackScore = averageScore([
      shotQuality('drives'),
      shotQuality('third_drives'),
      shotQuality('speedups'),
      shotSuccess('drives'),
      shotSuccess('third_drives'),
      shotSuccess('speedups')
    ]);
    const defenseScore = averageScore([
      shotQuality('dinks'),
      shotQuality('drops'),
      shotQuality('resets'),
      shotSuccess('dinks'),
      shotSuccess('drops'),
      shotSuccess('resets')
    ]);
    const consistencyScore = averageScore([
      100 - Number(stats.net_fault_percentage || 0),
      100 - Number(stats.out_fault_percentage || 0),
      shotSuccess('serves'),
      shotSuccess('returns')
    ]);
    const coverageScore = stats.average_x_coverage_percentage !== undefined
      ? 100 - Math.abs(Number(stats.average_x_coverage_percentage || 0) - 50) * 2
      : null;
    const agilityScore = averageScore([
      coverageScore,
      ((stats.kitchen_area || {}).outcome_stats || {}).success_percentage,
      ((stats.mid_court_area || {}).outcome_stats || {}).success_percentage
    ]);
    const qualityScore = normalizedScore(stats.average_shot_quality);
    const pbRatings = ((stats.trends || {}).ratings || stats.ratings || {});
    const skillRows = [
      {key: 'serve', label: t('ANALYSIS_SKILL_SERVE'), value: duprRating(pbRatings.serve !== undefined ? pbRatings.serve : serveScore)},
      {key: 'return', label: t('ANALYSIS_SKILL_RETURN'), value: duprRating(pbRatings.return !== undefined ? pbRatings.return : returnScore)},
      {key: 'offense', label: t('ANALYSIS_SKILL_OFFENSE'), value: duprRating(pbRatings.offense !== undefined ? pbRatings.offense : attackScore)},
      {key: 'defense', label: t('ANALYSIS_SKILL_DEFENSE'), value: duprRating(pbRatings.defense !== undefined ? pbRatings.defense : defenseScore)},
      {key: 'consistency', label: t('ANALYSIS_SKILL_CONSISTENCY'), value: duprRating(pbRatings.consistency !== undefined ? pbRatings.consistency : consistencyScore)},
      {key: 'agility', label: t('ANALYSIS_SKILL_AGILITY'), value: duprRating(pbRatings.agility !== undefined ? pbRatings.agility : agilityScore)}
    ];
    const skillRating = duprRating(pbRatings.overall !== undefined ? pbRatings.overall : averageScore([qualityScore, serveScore, returnScore, attackScore, defenseScore, consistencyScore]));
    const fastestShot = shotProfile
      .filter(shot => shot.maxSpeed !== null && shot.maxSpeed !== undefined)
      .sort((a, b) => Number(b.maxSpeed || 0) - Number(a.maxSpeed || 0))[0];
    const serveStats = stats.serves || {};
    const quickMetrics = [
      {label: t('ANALYSIS_AVERAGE_QUALITY'), value: qualityPercent(stats.average_shot_quality)},
      {label: t('ANALYSIS_SHOTS'), value: statValue(stats.shot_count)},
      {label: t('ANALYSIS_BEST_SHOT'), value: bestShot ? `${bestShot.label} ${qualityPercent(bestShot.quality)}` : '-'},
      {label: t('ANALYSIS_MOST_USED_SHOT'), value: mostUsedShot ? `${mostUsedShot.label} (${statValue(mostUsedShot.count)})` : '-'},
      {label: t('ANALYSIS_AVERAGE_SERVE'), value: formatSpeed(shotSpeedValue(serveStats, ['average_speed', 'avg_speed', 'mean_speed', 'average_mph', 'avg_mph']))},
      {label: t('ANALYSIS_FASTEST_SHOT'), value: fastestShot ? `${fastestShot.label} ${formatSpeed(fastestShot.maxSpeed)}` : '-'},
      {label: t('ANALYSIS_MAIN_ERROR'), value: Number(stats.net_fault_percentage || 0) >= Number(stats.out_fault_percentage || 0) ? `Net ${statPercent(stats.net_fault_percentage)}` : `Out ${statPercent(stats.out_fault_percentage)}`},
      {label: t('ANALYSIS_KITCHEN_SUCCESS'), value: statPercent(shotSuccessValue(stats.kitchen_area))}
    ].filter(metric => metric.value !== '-');
    return {
      player,
      stats,
      shotProfile,
      mostUsedShot,
      bestShot,
      skillRows,
      skillRating,
      fastestShot,
      quickMetrics
    };
  });
  const shotPerformanceFilterOptions = [
    {id: 'all', label: t('ALL'), type: 'all'},
    ...playersByTeam.map(team => ({
      id: `team-${Number(team.teamNumber) - 1}`,
      label: t('ANALYSIS_TEAM_NUMBER', {number: team.teamNumber}),
      type: 'team',
      teamNumber: Number(team.teamNumber) - 1
    })),
    ...players.map(player => ({
      id: `player-${player.id}`,
      label: displayPlayerName(player.id),
      type: 'player',
      playerId: player.id,
      teamNumber: Number(player.teamNumber || 1) - 1
    }))
  ];
  const selectedShotPerformanceFilter = shotPerformanceFilterOptions.find(option => option.id === shotPerformanceFilter) || shotPerformanceFilterOptions[0];
  const shotPerformanceStatsForFilter = (() => {
    if (selectedShotPerformanceFilter.type === 'team') {
      return statsPlayers.filter(player => Number(player.team) === Number(selectedShotPerformanceFilter.teamNumber));
    }
    if (selectedShotPerformanceFilter.type === 'player') {
      const stats = statsPlayers[selectedShotPerformanceFilter.playerId];
      return stats ? [stats] : [];
    }
    return statsPlayers.filter(Boolean);
  })();
  const shotExplorerPlayerIdsForPerformanceFilter = () => {
    if (selectedShotPerformanceFilter.type === 'team') {
      const teamGroup = playersByTeam.find(group => Number(group.teamNumber) === Number(selectedShotPerformanceFilter.teamNumber) + 1);
      return teamGroup ? teamGroup.players.map(player => player.id) : [];
    }
    if (selectedShotPerformanceFilter.type === 'player') {
      return [selectedShotPerformanceFilter.playerId];
    }
    return [];
  };
  const aggregateShotPerformance = (shot, playerStats) => {
    const count = playerStats.reduce((sum, player) => sum + Number(((player[shot.key] || {}).count) || 0), 0);
    const qualityValues = playerStats
      .map(player => (player[shot.key] || {}).average_quality)
      .filter(value => value !== null && value !== undefined);
    const successValues = playerStats
      .map(player => (((player[shot.key] || {}).outcome_stats || {}).success_percentage))
      .filter(value => value !== null && value !== undefined);
    const rallyWonValues = playerStats
      .map(player => (((player[shot.key] || {}).outcome_stats || {}).rally_won_percentage))
      .filter(value => value !== null && value !== undefined);
    const netFaultValues = playerStats
      .map(player => Number(player.net_fault_percentage))
      .filter(value => !Number.isNaN(value));
    const outFaultValues = playerStats
      .map(player => Number(player.out_fault_percentage))
      .filter(value => !Number.isNaN(value));
    return {
      count,
      quality: qualityValues.length > 0 ? qualityValues.reduce((sum, value) => sum + Number(value), 0) / qualityValues.length : null,
      success: successValues.length > 0 ? successValues.reduce((sum, value) => sum + Number(value), 0) / successValues.length : null,
      rallyWon: rallyWonValues.length > 0 ? rallyWonValues.reduce((sum, value) => sum + Number(value), 0) / rallyWonValues.length : null,
      netFault: netFaultValues.length > 0 ? netFaultValues.reduce((sum, value) => sum + value, 0) / netFaultValues.length : null,
      outFault: outFaultValues.length > 0 ? outFaultValues.reduce((sum, value) => sum + value, 0) / outFaultValues.length : null
    };
  };
  const trainingTips = (() => {
    const tips = [];
    playerStatCards.forEach(card => {
      const {player, stats, shotProfile, bestShot, skillRows} = card;
      const playerName = displayPlayerName(player.id);
      const usedShots = shotProfile.filter(shot => Number(shot.count || 0) >= 2);
      const weakestShot = usedShots
        .slice()
        .sort((a, b) => Number(a.quality || 0) - Number(b.quality || 0))[0];
      const mostUsedWeakShot = usedShots
        .filter(shot => Number(shot.quality || 0) > 0 && Number(shot.quality || 0) < 55)
        .sort((a, b) => Number(b.count || 0) - Number(a.count || 0))[0];
      const weakestSkill = skillRows
        .filter(row => row.value !== null && row.value !== undefined)
        .slice()
        .sort((a, b) => Number(a.value || 0) - Number(b.value || 0))[0];
      const netFault = Number(stats.net_fault_percentage || 0);
      const outFault = Number(stats.out_fault_percentage || 0);
      const kitchenSuccess = shotSuccessValue(stats.kitchen_area);
      const transitionSuccess = shotSuccessValue(stats.mid_court_area);
      const serveSuccess = shotSuccessValue(stats.serves);
      const returnSuccess = shotSuccessValue(stats.returns);

      if (mostUsedWeakShot) {
        tips.push({
          key: `${player.id}-used-weak-${mostUsedWeakShot.key}`,
          playerName,
          label: t('ANALYSIS_TIP_STABILIZE_SHOT', {shot: mostUsedWeakShot.label}),
          message: t('ANALYSIS_TIP_STABILIZE_SHOT_TEXT', {player: playerName, shot: mostUsedWeakShot.label.toLowerCase(), quality: qualityPercent(mostUsedWeakShot.quality)}),
          priority: 95
        });
      } else if (weakestShot) {
        tips.push({
          key: `${player.id}-weak-${weakestShot.key}`,
          playerName,
          label: t('ANALYSIS_TIP_TRAIN_SHOT', {shot: weakestShot.label}),
          message: t('ANALYSIS_TIP_TRAIN_SHOT_TEXT', {player: playerName, shot: weakestShot.label.toLowerCase(), quality: qualityPercent(weakestShot.quality)}),
          priority: 82
        });
      }

      if (netFault >= outFault && netFault >= 8) {
        tips.push({
          key: `${player.id}-net-fault`,
          playerName,
          label: t('ANALYSIS_TIP_NET_ERRORS'),
          message: t('ANALYSIS_TIP_NET_ERRORS_TEXT', {player: playerName, value: statPercent(netFault)}),
          priority: 88
        });
      } else if (outFault >= 8) {
        tips.push({
          key: `${player.id}-out-fault`,
          playerName,
          label: t('ANALYSIS_TIP_DEPTH_CONTROL'),
          message: t('ANALYSIS_TIP_DEPTH_CONTROL_TEXT', {player: playerName, value: statPercent(outFault)}),
          priority: 86
        });
      }

      if (kitchenSuccess !== undefined && kitchenSuccess !== null && Number(kitchenSuccess) < 50) {
        tips.push({
          key: `${player.id}-kitchen`,
          playerName,
          label: t('ANALYSIS_TIP_KITCHEN_PLAY'),
          message: t('ANALYSIS_TIP_KITCHEN_PLAY_TEXT', {player: playerName, value: statPercent(kitchenSuccess)}),
          priority: 80
        });
      } else if (transitionSuccess !== undefined && transitionSuccess !== null && Number(transitionSuccess) < 50) {
        tips.push({
          key: `${player.id}-transition`,
          playerName,
          label: t('ANALYSIS_TIP_TRANSITION_ZONE'),
          message: t('ANALYSIS_TIP_TRANSITION_ZONE_TEXT', {player: playerName, value: statPercent(transitionSuccess)}),
          priority: 78
        });
      }

      if (serveSuccess !== undefined && returnSuccess !== undefined && Number(returnSuccess) + 12 < Number(serveSuccess)) {
        tips.push({
          key: `${player.id}-return`,
          playerName,
          label: t('ANALYSIS_SKILL_RETURN'),
          message: t('ANALYSIS_TIP_RETURN_TEXT', {player: playerName, returnValue: statPercent(returnSuccess), serveValue: statPercent(serveSuccess)}),
          priority: 76
        });
      }

      if (bestShot && Number(bestShot.quality || 0) >= 70) {
        tips.push({
          key: `${player.id}-strength-${bestShot.key}`,
          playerName,
          label: t('ANALYSIS_TIP_STRENGTH', {shot: bestShot.label}),
          message: t('ANALYSIS_TIP_STRENGTH_TEXT', {player: playerName, shot: bestShot.label.toLowerCase(), quality: qualityPercent(bestShot.quality)}),
          priority: 65
        });
      } else if (weakestSkill) {
        tips.push({
          key: `${player.id}-skill-${weakestSkill.key}`,
          playerName,
          label: t('ANALYSIS_TIP_MONITOR_SKILL', {skill: weakestSkill.label}),
          message: t('ANALYSIS_TIP_MONITOR_SKILL_TEXT', {player: playerName, skill: weakestSkill.label.toLowerCase(), rating: formatRating(weakestSkill.value)}),
          priority: 62
        });
      }
    });

    return tips
      .sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0))
      .slice(0, 8);
  })();
  const shotPerformanceRows = GAME_STAT_SHOTS.map(shot => {
    const teamValues = [0, 1].map(teamNumber => {
      const teamPlayers = statsPlayers.filter(player => Number(player.team) === teamNumber);
      return aggregateShotPerformance(shot, teamPlayers);
    });
    return {
      ...shot,
      teamValues,
      scopeValue: aggregateShotPerformance(shot, shotPerformanceStatsForFilter)
    };
  });
  const strongestShot = shotPerformanceRows
    .slice()
    .sort((a, b) => Math.max(Number(b.teamValues[0].quality || 0), Number(b.teamValues[1].quality || 0)) - Math.max(Number(a.teamValues[0].quality || 0), Number(a.teamValues[1].quality || 0)))[0];
  const teamQualityLeader = statsPlayersByTeam
    .slice()
    .sort((a, b) => Number(b.avgQuality || 0) - Number(a.avgQuality || 0))[0];
  const teamKitchenLeader = statsPlayersByTeam
    .slice()
    .sort((a, b) => Number(b.kitchen || 0) - Number(a.kitchen || 0))[0];
  const lowestErrorTeam = statsPlayersByTeam
    .slice()
    .sort((a, b) => (Number(a.netFault || 0) + Number(a.outFault || 0)) - (Number(b.netFault || 0) + Number(b.outFault || 0)))[0];
  const gameInsights = [
    teamQualityLeader ? t('ANALYSIS_INSIGHT_TEAM_QUALITY', {team: teamQualityLeader.teamNumber, quality: qualityPercent(teamQualityLeader.avgQuality)}) : null,
    teamKitchenLeader ? t('ANALYSIS_INSIGHT_TEAM_KITCHEN', {team: teamKitchenLeader.teamNumber, quality: qualityPercent(teamKitchenLeader.kitchen)}) : null,
    lowestErrorTeam ? t('ANALYSIS_INSIGHT_TEAM_ERRORS', {team: lowestErrorTeam.teamNumber}) : null,
    strongestShot ? t('ANALYSIS_INSIGHT_STRONGEST_SHOT', {shot: strongestShot.label}) : null
  ].filter(Boolean);
  const matchStory = [
    t('ANALYSIS_MATCH_STORY_SCORE', {score: formatSegmentScore(statsGame.game_outcome || overview.score), rallies: statValue(overview.totalRallies)}),
    teamKitchenLeader ? t('ANALYSIS_MATCH_STORY_KITCHEN', {team: teamKitchenLeader.teamNumber}) : null,
    teamQualityLeader ? t('ANALYSIS_MATCH_STORY_QUALITY', {team: teamQualityLeader.teamNumber}) : null,
    strongestShot ? t('ANALYSIS_MATCH_STORY_SHOT', {shot: strongestShot.label}) : null
  ].filter(Boolean).join(' ');
  const courtZoneRows = [
    {key: 'near_baseline_area', label: t('ANALYSIS_ZONE_BASELINE'), description: t('ANALYSIS_ZONE_BASELINE_DESC')},
    {key: 'mid_court_area', label: t('ANALYSIS_ZONE_TRANSITION'), description: t('ANALYSIS_ZONE_TRANSITION_DESC')},
    {key: 'kitchen_area', label: 'Kitchen', description: t('ANALYSIS_ZONE_KITCHEN_DESC')},
    {key: 'near_left_sideline_area', label: t('ANALYSIS_ZONE_LEFT'), description: t('ANALYSIS_ZONE_LEFT_DESC')},
    {key: 'near_midline_area', label: t('ANALYSIS_ZONE_CENTER'), description: t('ANALYSIS_ZONE_CENTER_DESC')},
    {key: 'near_right_sideline_area', label: t('ANALYSIS_ZONE_RIGHT'), description: t('ANALYSIS_ZONE_RIGHT_DESC')}
  ].map(zone => {
    const teamValues = [0, 1].map(teamNumber => {
      const teamPlayers = statsPlayers.filter(player => Number(player.team) === teamNumber);
      const count = teamPlayers.reduce((sum, player) => sum + Number(((player[zone.key] || {}).count) || 0), 0);
      const successValues = teamPlayers
        .map(player => (((player[zone.key] || {}).outcome_stats || {}).success_percentage))
        .filter(value => value !== null && value !== undefined);
      return {
        count,
        success: successValues.length > 0 ? successValues.reduce((sum, value) => sum + Number(value), 0) / successValues.length : null
      };
    });
    const totalCount = teamValues.reduce((sum, value) => sum + Number(value.count || 0), 0);
    const avgSuccessValues = teamValues.map(value => value.success).filter(value => value !== null && value !== undefined);
    return {
      ...zone,
      teamValues,
      totalCount,
      avgSuccess: avgSuccessValues.length > 0 ? avgSuccessValues.reduce((sum, value) => sum + Number(value), 0) / avgSuccessValues.length : null
    };
  });
  const maxZoneCount = Math.max(...courtZoneRows.map(zone => Number(zone.totalCount || 0)), 1);
  const mainCourtZones = courtZoneRows.filter(zone => ['kitchen_area', 'mid_court_area', 'near_baseline_area'].includes(zone.key));
  const sideCourtZones = courtZoneRows.filter(zone => ['near_left_sideline_area', 'near_midline_area', 'near_right_sideline_area'].includes(zone.key));
  const displayScoreSegment = timelineRally;
  const displayScore = displayScoreSegment ? segmentScore(displayScoreSegment) : null;
  const scoreDraftForDisplay = scoreToDraft(displayScore);
  const scoreboardRows = [1, 2].map(teamNumber => {
    const teamGroup = playersByTeam.find(group => Number(group.teamNumber) === teamNumber);
    const teamNames = teamGroup && teamGroup.players.length > 0
      ? teamGroup.players.map(player => displayPlayerName(player.id)).join(' / ')
      : `Team ${teamNumber}`;
    return {
      teamNumber,
      name: teamNames,
      score: teamNumber === 1 ? scoreDraftForDisplay.team1 : scoreDraftForDisplay.team2
    };
  });
  const playerHasChanges = players.some(player => (playerNames[player.id] || '') !== (player.name || defaultPlayerName(player, t)));
  const selectedScore = selectedSegment ? segmentScore(selectedSegment) : overview.score;
  const selectedSegmentType = selectedSegment && selectedSegment.segmentType === 'highlight' ? 'highlights' : 'rallies';
  const selectedSegmentId = selectedSegment ? selectedSegment.id : null;
  const selectedPlayerFocus = playerStatCards.find(card => Number(card.player.id) === Number(selectedPlayerFocusId)) || playerStatCards[0] || null;
  const leaderboardRows = playerStatCards.map(card => {
    const playerRallyEndEvents = rallyEndEvents.filter(event => event.resultType && Number(event.finalPlayerId) === Number(card.player.id));
    const winningRallyEnds = playerRallyEndEvents.filter(event => event.resultType === 'winner');
    const errorRallyEnds = playerRallyEndEvents.filter(event => event.resultType === 'error');
    const servingArrival = (((card.stats.kitchen_arrival_percentage || {}).serving || {}).oneself || {});
    const servingArrivalPercentage = servingArrival.denominator ? (Number(servingArrival.numerator || 0) / Number(servingArrival.denominator || 1)) * 100 : null;
    const trendsAccuracy = ((card.stats.trends || {}).shot_accuracy || {});
    const inAccuracy = trendsAccuracy.in !== undefined ? Number(trendsAccuracy.in) * 100 : Math.max(0, 100 - Number(card.stats.net_fault_percentage || 0) - Number(card.stats.out_fault_percentage || 0));
    return {
      ...card,
      winningRallyEnds,
      errorRallyEnds,
      servingArrival,
      servingArrivalPercentage,
      shotAccuracy: {
        in: inAccuracy,
        net: trendsAccuracy.net !== undefined ? Number(trendsAccuracy.net) * 100 : Number(card.stats.net_fault_percentage || 0),
        out: trendsAccuracy.out !== undefined ? Number(trendsAccuracy.out) * 100 : Number(card.stats.out_fault_percentage || 0)
      }
    };
  }).sort((a, b) => Number(b.skillRating || 0) - Number(a.skillRating || 0));
  const teamFinishRows = [0, 1].map(teamIndex => {
    const teamPlayerIds = players
      .filter(player => Number(playerTeamIndex(player)) === Number(teamIndex))
      .map(player => Number(player.id));
    const playerRows = leaderboardRows.filter(row => teamPlayerIds.includes(Number(row.player.id)));
    const winners = playerRows.reduce((sum, row) => sum + row.winningRallyEnds.length, 0);
    const errors = playerRows.reduce((sum, row) => sum + row.errorRallyEnds.length, 0);
    return {
      teamNumber: teamIndex + 1,
      playerRows,
      winners,
      errors,
      total: winners + errors
    };
  });
  const scoreHasChanges = selectedSegment
    ? scoreDraft.team1 !== scoreToDraft(selectedScore).team1 || scoreDraft.team2 !== scoreToDraft(selectedScore).team2
    : false;

  const changePlayerName = playerId => event => {
    const value = event.target.value;
    setPlayerNames(current => ({
      ...current,
      [playerId]: value
    }));
  };

  const savePlayerNames = async event => {
    event.preventDefault();
    if (!analysisRequestId) {
      return;
    }
    setSavingPlayers(true);
    setPlayerSaveError(null);
    const payload = {};
    players.forEach(player => {
      const name = (playerNames[player.id] || '').trim();
      payload[player.id] = {name};
    });
    const result = await api.updateVideoAnalysisPlayers(analysisRequestId, payload, token);
    setSavingPlayers(false);
    if (result.status >= 200 && result.status < 300) {
      setReport(result.json);
      const nextNames = {};
      (result.json.players || []).forEach(player => {
        nextNames[player.id] = player.name || defaultPlayerName(player, t);
      });
      setPlayerNames(nextNames);
      return;
    }
    setPlayerSaveError(t('ANALYSIS_PLAYER_NAMES_SAVE_ERROR'));
  };

  const changeScore = key => event => {
    const value = event.target.value.replace(/[^\d]/g, '').slice(0, 3);
    setScoreDraft(current => ({
      ...current,
      [key]: value
    }));
  };

  const resetShotExplorerPlayback = () => {
    setActivePlaylistIndex(0);
    if (playbackMode === 'shot-explorer') {
      clearSegmentIntroTimer();
      setSegmentIntro(null);
      setActiveSegment(null);
    }
  };

  const toggleShotExplorerPlayer = playerId => event => {
    event.preventDefault();
    resetShotExplorerPlayback();
    setShotExplorerPlayers(current => (
      current.includes(playerId)
        ? current.filter(id => id !== playerId)
        : [...current, playerId]
    ));
  };

  const toggleShotExplorerType = filterId => event => {
    event.preventDefault();
    resetShotExplorerPlayback();
    setShotExplorerTypes(current => (
      current.includes(filterId)
        ? current.filter(id => id !== filterId)
        : [...current, filterId]
    ));
  };

  const changeShotExplorerQuality = key => event => {
    const value = Math.max(0, Math.min(100, Number(event.target.value || 0)));
    resetShotExplorerPlayback();
    setShotExplorerQuality(current => {
      const next = {...current, [key]: value};
      if (next.min > next.max) {
        return key === 'min' ? {...next, max: value} : {...next, min: value};
      }
      return next;
    });
  };

  const clearShotExplorerFilters = event => {
    event.preventDefault();
    resetShotExplorerPlayback();
    setShotExplorerPlayers([]);
    setShotExplorerTypes([]);
    setShotExplorerQuality(DEFAULT_SHOT_QUALITY_FILTER);
  };

  const toggleShotFilterDraftPlayer = playerId => event => {
    event.preventDefault();
    setShotFilterDraftPlayers(current => (
      current.includes(playerId)
        ? current.filter(id => id !== playerId)
        : [...current, playerId]
    ));
  };

  const toggleShotFilterDraftType = filterId => event => {
    event.preventDefault();
    setShotFilterDraftTypes(current => (
      current.includes(filterId)
        ? current.filter(id => id !== filterId)
        : [...current, filterId]
    ));
  };

  const changeShotFilterDraftQuality = key => event => {
    const value = Math.max(0, Math.min(100, Number(event.target.value || 0)));
    setShotFilterDraftQuality(current => {
      const next = {...current, [key]: value};
      if (next.min > next.max) {
        return key === 'min' ? {...next, max: value} : {...next, min: value};
      }
      return next;
    });
  };

  const clearShotFilterDrafts = event => {
    event.preventDefault();
    setShotFilterDraftPlayers([]);
    setShotFilterDraftTypes([]);
    setShotFilterDraftQuality(DEFAULT_SHOT_QUALITY_FILTER);
    setShotFilterDraftPlaybackScope('shot');
  };

  const changeShotExplorerPlaybackScope = nextScope => event => {
    event.preventDefault();
    setShotExplorerPlaybackScope(nextScope);
    setActivePlaylistIndex(0);
    if (playbackMode !== 'shot-explorer') {
      return;
    }
    const nextSegments = nextScope === 'shot' ? shotExplorerClipSegments : shotExplorerResults.map(item => item.rally);
    if (nextSegments.length > 0) {
      playSegmentWithIntro(nextSegments[0], 'shot-explorer', 0);
    }
  };

  const openShotExplorerPlaylist = event => {
    event.preventDefault();
    setAnalysisView('shot-explorer');
    setPlaybackMode('shot-explorer');
    setShotExplorerPlaybackScope('shot');
    setActivePlaylistIndex(0);
    const nextSegment = shotExplorerClipSegments[0];
    if (nextSegment) {
      playSegmentWithIntro(nextSegment, 'shot-explorer', 0);
    }
  };

  const applyShotFilters = event => {
    event.preventDefault();
    const nextQuality = {...shotFilterDraftQuality};
    const nextResults = buildShotExplorerResults({
      rallies,
      shots,
      rallyEndEvents,
      players: shotFilterDraftPlayers,
      types: shotFilterDraftTypes,
      quality: nextQuality
    });
    const nextClipSegments = buildShotExplorerClipSegments(nextResults, durationMs, t);
    const nextSegments = shotFilterDraftPlaybackScope === 'shot'
      ? nextClipSegments
      : nextResults.map(item => item.rally);
    setShotExplorerPlayers(shotFilterDraftPlayers);
    setShotExplorerTypes(shotFilterDraftTypes);
    setShotExplorerQuality(nextQuality);
    setShotExplorerPlaybackScope(shotFilterDraftPlaybackScope);
    setAnalysisView('shot-explorer');
    setPlaybackMode('shot-explorer');
    setActivePlaylistIndex(0);
    if (nextSegments.length > 0) {
      playSegmentWithIntro(nextSegments[0], 'shot-explorer', 0);
    }
  };

  const disableShotFilters = event => {
    event.preventDefault();
    const nextQuality = {...DEFAULT_SHOT_QUALITY_FILTER};
    const nextResults = buildShotExplorerResults({
      rallies,
      shots,
      rallyEndEvents,
      players: [],
      types: [],
      quality: nextQuality
    });
    const nextSegments = buildShotExplorerClipSegments(nextResults, durationMs, t);
    setShotExplorerPlayers([]);
    setShotExplorerTypes([]);
    setShotExplorerQuality(nextQuality);
    setShotFilterDraftPlayers([]);
    setShotFilterDraftTypes([]);
    setShotFilterDraftQuality(nextQuality);
    setShotFilterDraftPlaybackScope('shot');
    setShotExplorerPlaybackScope('shot');
    setShotFilterPanelOpen(false);
    setAnalysisView('shot-explorer');
    setPlaybackMode('shot-explorer');
    setActivePlaylistIndex(0);
    if (nextSegments.length > 0) {
      playSegmentWithIntro(nextSegments[0], 'shot-explorer', 0);
    }
  };

  const shareSidePlaylist = event => {
    event.preventDefault();
    if (activePlaylistMode === 'full') {
      setAnalysisSharingTarget({
        label: 'Analisi completa',
        description: "Stai condividendo l'analisi completa. Il link aprira la pagina Analisi Replayer con la partita intera disponibile."
      });
      setAnalysisSharingModalVisible(true);
      return;
    }
    if (activePlaylistMode === 'highlights') {
      setAnalysisSharingTarget({
        mode: 'highlights',
        segment: orderedHighlights[0] ? orderedHighlights[0].id : '',
        label: 'Lista AI Highlights',
        description: "Stai condividendo la lista degli highlight. Il link aprira l'analisi Replayer partendo dal primo highlight."
      });
      setAnalysisSharingModalVisible(true);
      return;
    }
    if (activePlaylistMode === 'shots') {
      setAnalysisSharingTarget({
        mode: 'shot-list',
        players: shotExplorerPlayers.join(','),
        types: shotExplorerTypes.join(','),
        qmin: String(shotExplorerQuality.min),
        qmax: String(shotExplorerQuality.max),
        scope: shotExplorerPlaybackScope,
        label: hasAppliedShotFilters
          ? `Lista ${shotExplorerPlaybackScope === 'shot' ? 'colpi' : 'rally'} filtrata`
          : `Lista ${shotExplorerPlaybackScope === 'shot' ? 'colpi' : 'rally'}`,
        description: hasAppliedShotFilters
          ? "Stai condividendo questa selezione filtrata. Il link aprira l'analisi Replayer con gli stessi filtri applicati."
          : `Stai condividendo la lista ${shotExplorerPlaybackScope === 'shot' ? 'dei colpi' : 'dei rally'} dello Shot Explorer.`
      });
      setAnalysisSharingModalVisible(true);
      return;
    }
    if (activePlaylistMode === 'shot-clips') {
      const firstSegment = shotClipSegments[0] || {};
      setAnalysisSharingTarget({
        mode: 'rally-end-list',
        playerId: firstSegment.playerId,
        resultType: firstSegment.resultType,
        label: sidePlaylistTitle,
        description: `Stai condividendo la lista ${sidePlaylistTitle.toLowerCase()}. Il link aprira l'analisi Replayer partendo dal primo colpo della selezione.`
      });
      setAnalysisSharingModalVisible(true);
      return;
    }
    setAnalysisSharingTarget({
      mode: 'rallies',
      segment: rallies[0] ? rallies[0].id : '',
      label: 'Lista rally',
      description: "Stai condividendo la lista dei rally. Il link aprira l'analisi Replayer partendo dal primo rally."
    });
    setAnalysisSharingModalVisible(true);
  };

  const openDownloadSelection = event => {
    event.preventDefault();
    setDownloadError(null);
    setDownloadResult(null);
    setDownloadSelectionMode(true);
    setDownloadSelectedKeys(downloadItems.map(item => item.key));
  };

  const cancelDownloadSelection = event => {
    event.preventDefault();
    setDownloadSelectionMode(false);
    setDownloadSelectedKeys([]);
    setDownloadError(null);
    setDownloadResult(null);
  };

  const toggleAllDownloadItems = event => {
    event.preventDefault();
    setDownloadSelectedKeys(allDownloadItemsSelected ? [] : downloadItems.map(item => item.key));
  };

  const toggleDownloadItem = item => event => {
    event.preventDefault();
    event.stopPropagation();
    setDownloadSelectedKeys(current => (
      current.includes(item.key)
        ? current.filter(key => key !== item.key)
        : [...current, item.key]
    ));
  };

  const pollDownloadStatus = (jobId, attempt = 0) => {
    if (!analysisDownloadRequestId || !jobId) {
      return;
    }
    if (downloadPollTimerRef.current) {
      clearTimeout(downloadPollTimerRef.current);
    }
    downloadPollTimerRef.current = setTimeout(async () => {
      const result = await api.getVideoAnalysisDownload(analysisDownloadRequestId, jobId, token);
      if (result.status >= 200 && result.status < 300) {
        const status = result.json.downloadStatus || result.json.status;
        setDownloadResult(result.json || {});
        if (status === 'finished' || status === 'error') {
          setDownloadPreparing(false);
          return;
        }
        pollDownloadStatus(jobId, attempt + 1);
        return;
      }
      if (attempt < 20) {
        pollDownloadStatus(jobId, attempt + 1);
        return;
      }
      setDownloadPreparing(false);
      setDownloadError(`Non e' stato possibile verificare il download: ${result.json && result.json.error ? result.json.error : result.status}`);
    }, attempt < 3 ? 2500 : 5000);
  };

  const prepareDownload = async event => {
    event.preventDefault();
    if (!analysisDownloadRequestId || selectedDownloadItems.length === 0 || downloadPreparing) {
      return;
    }
    setDownloadPreparing(true);
    setDownloadError(null);
    setDownloadResult(null);
    const payload = {
      title: sidePlaylistTitle,
      mode: activePlaylistMode,
      playbackMode,
      segments: selectedDownloadItems.map((item, index) => ({
        title: item.title,
        startMs: Math.round(item.startMs),
        endMs: Math.round(item.endMs),
        order: index,
        sourceIndex: item.sourceIndex,
        segmentType: item.segmentType,
        rallyId: item.rallyId,
        highlightId: item.highlightId
      }))
    };
    const result = await api.createVideoAnalysisDownload(analysisDownloadRequestId, payload, token);
    if (result.status >= 200 && result.status < 300) {
      setDownloadResult(result.json || {});
      const status = result.json.downloadStatus || result.json.status;
      if (result.json.downloadUrl || status === 'finished' || status === 'error') {
        setDownloadPreparing(false);
        return;
      }
      if (result.json.downloadJobId) {
        pollDownloadStatus(result.json.downloadJobId);
        return;
      }
      setDownloadPreparing(false);
      return;
    }
    setDownloadPreparing(false);
    setDownloadError(t('DOWNLOAD_PREPARATION_ERROR', {reason: result.json && result.json.error ? result.json.error : result.status}));
  };

  const renderDownloadToggle = (segment, index) => {
    if (!downloadSelectionMode) {
      return null;
    }
    const item = downloadItems.find(entry => entry.key === downloadSegmentKey(segment, index));
    if (!item) {
      return null;
    }
    const selected = downloadSelectedKeys.includes(item.key);
    return (
      <span
        className={`analysis-download-check ${selected ? 'selected' : ''}`}
        onClick={toggleDownloadItem(item)}
        title={selected ? t('REMOVE_FROM_DOWNLOAD') : t('ADD_TO_DOWNLOAD')}
      >
        {selected ? '✓' : ''}
      </span>
    );
  };

  const toggleShotFilterPanel = event => {
    event.preventDefault();
    setShotFilterPanelOpen(current => {
      const nextOpen = !current;
      if (nextOpen) {
        setShotFilterDraftPlayers(shotExplorerPlayers);
        setShotFilterDraftTypes(shotExplorerTypes);
        setShotFilterDraftQuality({...shotExplorerQuality});
        setShotFilterDraftPlaybackScope(shotExplorerPlaybackScope === 'rally' ? 'rally' : 'shot');
      }
      return nextOpen;
    });
  };

  const openShotExplorerForPlayer = playerId => event => {
    event.preventDefault();
    setAnalysisView('shot-explorer');
    setShotExplorerPlayers([playerId]);
    setShotExplorerTypes([]);
  };

  const openPlayerFocus = playerId => event => {
    event.preventDefault();
    setSelectedPlayerFocusId(playerId);
    setAnalysisView('player-focus');
  };

  const openShotExplorerForShot = filterId => event => {
    event.preventDefault();
    setAnalysisView('shot-explorer');
    setShotExplorerPlayers(shotExplorerPlayerIdsForPerformanceFilter());
    setShotExplorerTypes(filterId ? [filterId] : []);
  };

  const openShotExplorerForPlayerShot = (playerId, filterId) => event => {
    event.preventDefault();
    setAnalysisView('shot-explorer');
    setShotExplorerPlayers([playerId]);
    setShotExplorerTypes(filterId ? [filterId] : []);
  };

  const playRallyEndClips = (playerId, resultType) => event => {
    event.preventDefault();
    const segments = buildRallyEndClipSegments(playerId, resultType);
    setShotClipSegments(segments);
    setPlaybackMode('shot-clips');
    setActivePlaylistIndex(0);
    if (segments.length > 0) {
      playSegmentWithIntro(segments[0], 'shot-clips', 0);
    }
  };

  const persistSegmentScore = async nextDraft => {
    if (!analysisRequestId || selectedSegmentId === null || selectedSegmentId === undefined) {
      return;
    }
    const nextScores = {
      rallies: {...((report.segmentScores || {}).rallies || {})},
      highlights: {...((report.segmentScores || {}).highlights || {})}
    };
    const nextScore = {
      team1: nextDraft.team1.trim(),
      team2: nextDraft.team2.trim()
    };
    if (nextScore.team1 || nextScore.team2) {
      nextScores[selectedSegmentType][String(selectedSegmentId)] = nextScore;
    } else {
      delete nextScores[selectedSegmentType][String(selectedSegmentId)];
    }

    setSavingScore(true);
    setScoreSaveError(null);
    const result = await api.updateVideoAnalysisSegmentScores(analysisRequestId, nextScores, token);
    setSavingScore(false);
    if (result.status >= 200 && result.status < 300) {
      setReport(result.json);
      const updatedSegments = selectedSegmentType === 'highlights' ? result.json.highlights || [] : result.json.rallies || [];
      const updatedSegment = updatedSegments.find(segment => Number(segment.id) === Number(selectedSegmentId));
      if (updatedSegment) {
        setActiveSegment(updatedSegment);
        setScoreDraft(scoreToDraft(segmentScore(updatedSegment)));
      }
      return;
    }
    setScoreSaveError(t('ANALYSIS_SCORE_SAVE_ERROR'));
  };

  const saveSegmentScore = async event => {
    event.preventDefault();
    await persistSegmentScore(scoreDraft);
  };

  const restoreProviderScore = event => {
    event.preventDefault();
    persistSegmentScore({team1: '', team2: ''});
  };

  const toggleVideoPlayback = event => {
    if (event) {
      event.preventDefault();
    }
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (video.paused) {
      const segmentToPlay = playbackMode !== 'full'
        ? activeSegment || playlistSegments[activePlaylistIndex] || timelineRally || rallies[0]
        : null;
      if (segmentToPlay) {
        const startMs = Number(segmentToPlay.startMs || 0);
        const endMs = Number(segmentToPlay.endMs || startMs);
        const currentVideoMs = video.currentTime * 1000;
        if (currentVideoMs < startMs || currentVideoMs >= endMs) {
          video.currentTime = startMs / 1000;
          setCurrentMs(startMs);
        }
        if (!activeSegment || activeSegment.id !== segmentToPlay.id) {
          setActiveSegment(segmentToPlay);
        }
      }
      video.playbackRate = playbackRate;
      video.play().catch(() => {});
      return;
    }
    video.pause();
  };

  const handleVideoLoadedMetadata = () => {
    const video = videoRef.current;
    if (!video || playbackMode === 'full') {
      return;
    }
    const initialSegment = activeSegment || playlistSegments[activePlaylistIndex] || rallies[0];
    if (!initialSegment) {
      return;
    }
    const startMs = Number(initialSegment.startMs || 0);
    video.currentTime = startMs / 1000;
    video.playbackRate = playbackRate;
    setCurrentMs(startMs);
    if (!activeSegment || Number(activeSegment.id) !== Number(initialSegment.id)) {
      setActiveSegment(initialSegment);
      setScoreDraft(scoreToDraft(segmentScore(initialSegment)));
    }
    const hasDeepLinkMode = new URLSearchParams(window.location.search || '').has('mode');
    if (!initialAutoplayRef.current && playbackMode === DEFAULT_ANALYSIS_PLAYBACK_MODE && !hasDeepLinkMode) {
      initialAutoplayRef.current = true;
      if (isSharedAnalysis) {
        video.muted = true;
      }
      video.play().catch(() => {});
    }
  };

  const seekBySeconds = seconds => event => {
    if (event) {
      event.preventDefault();
    }
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const nextTime = Math.max(0, Math.min((durationMs || 0) / 1000, video.currentTime + seconds));
    video.currentTime = nextTime;
    setCurrentMs(nextTime * 1000);
  };

  const stepTechnicalSegment = seconds => event => {
    if (event) {
      event.preventDefault();
    }
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.pause();
    const nextMs = Math.max(technicalStartMs, Math.min(technicalEndMs, (video.currentTime * 1000) + (seconds * 1000)));
    video.currentTime = nextMs / 1000;
    setCurrentMs(nextMs);
    setIsVideoPlaying(false);
  };

  const seekFromProgress = event => {
    const video = videoRef.current;
    if (!video || !durationMs) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const nextMs = ratio * durationMs;
    video.currentTime = nextMs / 1000;
    setCurrentMs(nextMs);
  };

  const seekFromTechnicalProgress = event => {
    const video = videoRef.current;
    if (!video || !technicalDurationMs) {
      return;
    }
    const rect = event.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
    const nextMs = technicalStartMs + (ratio * technicalDurationMs);
    video.pause();
    video.currentTime = nextMs / 1000;
    setCurrentMs(nextMs);
    setIsVideoPlaying(false);
  };

  const changePlaybackRate = event => {
    event.preventDefault();
    const video = videoRef.current;
    const nextRate = playbackRate === 1 ? 0.75 : playbackRate === 0.75 ? 0.5 : playbackRate === 0.5 ? 0.25 : 1;
    setPlaybackRate(nextRate);
    if (video) {
      video.playbackRate = nextRate;
    }
  };

  const toggleFullscreen = event => {
    event.preventDefault();
    const video = videoRef.current;
    const shell = video ? video.closest('.analysis-video-shell') : null;
    if (!shell) {
      return;
    }
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
      return;
    }
    if (shell.requestFullscreen) {
      shell.requestFullscreen().catch(() => {
        if (video && video.webkitEnterFullscreen) {
          video.webkitEnterFullscreen();
        }
      });
      return;
    }
    if (video && video.webkitEnterFullscreen) {
      video.webkitEnterFullscreen();
    }
  };

  const segmentDisplayTitle = segment => (
    segment && (segment.displayTitle || translateText(segment.title || `Rally ${segment.number || Number(segment.rallyIndex) + 1 || ''}`, t))
  );

  const clearSegmentIntroTimer = () => {
    if (segmentIntroTimerRef.current) {
      clearTimeout(segmentIntroTimerRef.current);
      segmentIntroTimerRef.current = null;
    }
    if (segmentIntroHideTimerRef.current) {
      clearTimeout(segmentIntroHideTimerRef.current);
      segmentIntroHideTimerRef.current = null;
    }
  };

  const jumpToSegment = (segment, shouldPlay = true, keepIntro = false) => {
    if (!keepIntro) {
      clearSegmentIntroTimer();
      setSegmentIntro(null);
    }
    const video = videoRef.current;
    if (!video || !segment) {
      return;
    }
    const startMs = Number(segment.startMs || 0);
    video.currentTime = startMs / 1000;
    video.playbackRate = playbackRate;
    setCurrentMs(startMs);
    setActiveSegment(segment);
    setScoreDraft(scoreToDraft(segmentScore(segment)));
    setScoreSaveError(null);
    if (shouldPlay) {
      if (isSharedAnalysis) {
        video.muted = true;
      }
      video.play().catch(() => {});
    }
  };

  const playSegmentWithIntro = (segment, nextMode, nextIndex = null) => {
    if (!segment) {
      return;
    }
    clearSegmentIntroTimer();
    if (nextMode) {
      setPlaybackMode(nextMode);
    }
    if (nextIndex !== null && nextIndex !== undefined) {
      setActivePlaylistIndex(nextIndex);
    }
    setActiveSegment(segment);
    setCurrentMs(Number(segment.startMs || 0));
    setScoreDraft(scoreToDraft(segmentScore(segment)));
    setScoreSaveError(null);
    setSegmentIntro({
      title: segmentDisplayTitle(segment),
      score: formatSegmentScore(segmentScore(segment)),
      type: nextMode === 'highlights' ? 'Highlight' : nextMode === 'shot-clips' ? 'Colpo' : 'Rally'
    });
    segmentIntroTimerRef.current = setTimeout(() => {
      segmentIntroTimerRef.current = null;
      jumpToSegment(segment, true, true);
    }, SEGMENT_INTRO_SEEK_DELAY_MS);
    segmentIntroHideTimerRef.current = setTimeout(() => {
      segmentIntroHideTimerRef.current = null;
      setSegmentIntro(null);
    }, SEGMENT_INTRO_MS);
  };

  const seekTo = (segment, shouldPlay = true, nextMode = null, nextIndex = null) => event => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (shouldPlay && nextMode && nextMode !== 'full') {
      playSegmentWithIntro(segment, nextMode, nextIndex);
      return;
    }
    if (nextMode) {
      setPlaybackMode(nextMode);
    }
    if (nextIndex !== null && nextIndex !== undefined) {
      setActivePlaylistIndex(nextIndex);
    }
    jumpToSegment(segment, shouldPlay);
  };

  const changePlaybackMode = nextMode => event => {
    if (event) {
      event.preventDefault();
    }
    setPlaybackMode(nextMode);
    setActivePlaylistIndex(0);
    if (nextMode === 'full') {
      clearSegmentIntroTimer();
      setSegmentIntro(null);
      setActiveSegment(null);
      return;
    }
    const nextSegments = nextMode === 'highlights' ? orderedHighlights : rallies;
    if (nextSegments.length > 0) {
      playSegmentWithIntro(nextSegments[0], nextMode, 0);
    }
  };

  const seekRallyByDelta = delta => event => {
    if (event) {
      event.preventDefault();
    }
    const sourceSegments = playbackMode === 'shot-explorer' && shotExplorerPlaylist.length > 0 ? shotExplorerPlaylist : rallies;
    if (sourceSegments.length === 0) {
      return;
    }
    const sourceIndex = playbackMode === 'shot-explorer'
      ? activePlaylistIndex
      : activeRally
      ? sourceSegments.findIndex(rally => Number(rally.id) === Number(activeRally.id))
      : -1;
    const baseIndex = sourceIndex >= 0 ? sourceIndex : activeRallyIndex >= 0 ? activeRallyIndex : 0;
    const nextIndex = Math.max(0, Math.min(sourceSegments.length - 1, baseIndex + delta));
    playSegmentWithIntro(sourceSegments[nextIndex], playbackMode === 'shot-explorer' ? 'shot-explorer' : 'rallies', nextIndex);
  };

  const replaySegment = event => {
    if (event) {
      event.preventDefault();
    }
    const segment = selectedSegment || rallies[0];
    if (segment) {
      jumpToSegment(segment);
    }
  };

  const replaySegmentSlow = event => {
    if (event) {
      event.preventDefault();
    }
    const segment = selectedSegment || rallies[0];
    if (!segment) {
      return;
    }
    setPlaybackRate(0.5);
    const video = videoRef.current;
    if (video) {
      video.playbackRate = 0.5;
    }
    jumpToSegment(segment, true);
  };

  const shareAnalysis = event => {
    event.preventDefault();
    setAnalysisSharingTarget(null);
    setAnalysisSharingModalVisible(true);
  };

  const shareAnalysisTarget = target => event => {
    event.preventDefault();
    event.stopPropagation();
    setAnalysisSharingTarget(target);
    setAnalysisSharingModalVisible(true);
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const nextMs = video.currentTime * 1000;
    setCurrentMs(nextMs);
    if (activeSegment && activeSegment.endMs && nextMs >= activeSegment.endMs) {
      if (segmentIntroTimerRef.current) {
        return;
      }
      if (playbackMode !== 'full' && playlistSegments.length > 0) {
        const currentIndex = playlistSegments.findIndex(segment => segment.id === activeSegment.id);
        const baseIndex = currentIndex >= 0 ? currentIndex : activePlaylistIndex;
        const nextIndex = baseIndex + 1;
        if (nextIndex < playlistSegments.length) {
          playSegmentWithIntro(playlistSegments[nextIndex], playbackMode, nextIndex);
          return;
        }
      }
      video.pause();
      setIsVideoPlaying(false);
      setActiveSegment(null);
    }
  };

  return (
    <>
    <div className="analysis-page">
      <div className="analysis-mobile-orientation-warning">
        {t('ANALYSIS_ROTATE_PHONE')}
      </div>
      <aside className="analysis-page-sidebar">
        <button type="button" className="analysis-page-back" onClick={() => history.push(isPartnerAnalysis ? '/partner-video' : '/player-video')}>
          {t('ANALYSIS_BACK_TO_REPLAYER')}
        </button>
        <nav className="analysis-page-nav">
          <button type="button" className={analysisView === 'home' ? 'active' : ''} onClick={() => setAnalysisView('home')}>
            <FaHome/><span><strong>{t('HOME')}</strong><small>{t('ANALYSIS_PLAYERS_OVERVIEW')}</small></span>
          </button>
          <button type="button" className={analysisView === 'shot-explorer' ? 'active' : ''} onClick={() => setAnalysisView('shot-explorer')}>
            <FaSearch/><span><strong>{t('ANALYSIS_SHOT_EXPLORER')}</strong><small>{t('ANALYSIS_FILTER_SHOTS_RALLIES')}</small></span>
          </button>
          <button type="button" className={analysisView === 'game-stats' ? 'active' : ''} onClick={() => setAnalysisView('game-stats')}>
            <FaChartBar/><span><strong>{t('ANALYSIS_GAME_STATS')}</strong><small>{t('ANALYSIS_MATCH_STATS')}</small></span>
          </button>
          <button type="button" className={analysisView === 'player-focus' ? 'active' : ''} onClick={() => { setSelectedPlayerFocusId(selectedPlayerFocus ? selectedPlayerFocus.player.id : null); setAnalysisView('player-focus'); }}>
            <FaUser/><span><strong>{t('ANALYSIS_PLAYER_FOCUS')}</strong><small>{t('ANALYSIS_PLAYER_DETAIL')}</small></span>
          </button>
          <button type="button" className={analysisView === 'leaderboards' ? 'active' : ''} onClick={() => setAnalysisView('leaderboards')}>
            <FaTrophy/><span><strong>{t('ANALYSIS_LEADERBOARDS')}</strong><small>{t('ANALYSIS_PERFORMANCE_RANKING')}</small></span>
          </button>
          <button type="button" className={analysisView === 'team-stats' ? 'active' : ''} onClick={() => setAnalysisView('team-stats')}>
            <FaUsers/><span><strong>{t('ANALYSIS_TEAM_STATS')}</strong><small>{t('ANALYSIS_TEAM_COMPARISON')}</small></span>
          </button>
        </nav>
        <div className="analysis-page-sidebar-title">{t('ANALYSIS_PLAYERS')} <b>{players.length}</b></div>
        <div className="analysis-page-player-list">
          {players.map(player => (
            <button type="button" className={`analysis-page-player-pill ${analysisView === 'player-focus' && Number(selectedPlayerFocusId) === Number(player.id) ? 'active' : ''}`} key={player.id} onClick={openPlayerFocus(player.id)}>
              {player.avatarUrl ? <img src={player.avatarUrl} alt={displayPlayerName(player.id)}/> : <span/>}
              <span className="analysis-page-player-copy">
                <strong>{displayPlayerName(player.id)}</strong>
                <small>{t('ANALYSIS_TEAM_NUMBER', {number: player.teamNumber || '-'})} · {t('ANALYSIS_PLAYER_NUMBER', {number: player.teamPlayerNumber || '-'})}</small>
              </span>
            </button>
          ))}
        </div>
      </aside>
      <main className="analysis-page-content">
        <div className="analysis-page-topbar">
          <div className="analysis-page-title-block">
            <button type="button" className="analysis-share-button" onClick={shareAnalysis} title={t('ANALYSIS_SHARE')} disabled={!report || !token}>
              <FaShareAlt/>
            </button>
            <div>
              <span>{t('ANALYSIS_REPLAYER')}</span>
              <h1>{report ? report.title : t('ANALYSIS_MATCH')}</h1>
            </div>
          </div>
          {report && report.providerWebpageUrl ? (
            <a href={report.providerWebpageUrl} target="_blank" rel="noopener noreferrer" className="analysis-provider-link">
              {t('ANALYSIS_OPEN_PB_VISION')}
            </a>
          ) : null}
        </div>
        {loading ? (
          <div className="analysis-report-loading">
            <Spinner animation="border" size="sm" className="mr-2"/>
            {t('ANALYSIS_LOADING_REPLAYER')}
          </div>
        ) : null}
        {error ? <Alert variant="danger">{error}</Alert> : null}
        {report && (!report.hasInsights || !report.hasStats) ? (
          <Alert variant="warning">
            {t('ANALYSIS_INCOMPLETE_DATA_HELP')}
          </Alert>
        ) : null}
        {report ? (
          <div className="analysis-report">
            <div className="analysis-report-hero">
              <div>
                <h4>{report.title}</h4>
                {report.description ? <p>{report.description}</p> : null}
              </div>
              <div className="analysis-report-provider">
                <span>{t('ANALYSIS_VIDEO_ID')}</span>
                <strong>{formatValue(report.providerVideoId)}</strong>
              </div>
            </div>
            <div className="analysis-viewer">
              <div className="analysis-viewer-main">
                <div className={`analysis-video-shell ${isSimulatedFullscreen ? 'simulated-fullscreen' : ''}`}>
                  {isSimulatedFullscreen ? (
                    <button type="button" className="analysis-fullscreen-close" onClick={toggleFullscreen} title={t('ANALYSIS_EXIT_FULLSCREEN')}>
                      ×
                    </button>
                  ) : null}
                  {videoUrl ? (
                    <video
                      ref={videoRef}
                      src={videoUrl}
                      playsInline
                      onLoadedMetadata={handleVideoLoadedMetadata}
                      onTimeUpdate={handleTimeUpdate}
                      onPlay={() => setIsVideoPlaying(true)}
                      onPause={() => setIsVideoPlaying(false)}
                    />
                  ) : (
                    <div className="analysis-video-empty">
                      {t('ANALYSIS_VIDEO_UNAVAILABLE')}
                    </div>
                  )}
                  {activeRally ? (
                    <div className="analysis-video-badge">{t('ANALYSIS_RALLY_NUMBER', {number: activeRally.number})}</div>
                  ) : null}
                  {formatSegmentScore(displayScore) !== '-' ? (
                    <div className="analysis-video-score">
                      {scoreboardRows.map(row => (
                        <div className="analysis-video-score-row" key={row.teamNumber}>
                          <span>{row.name}</span>
                          <strong>{row.score || '0'}</strong>
                        </div>
                      ))}
                    </div>
                  ) : null}
                  {segmentIntro ? (
                    <div className="analysis-segment-intro">
                      <strong>{segmentIntro.title}</strong>
                      {segmentIntro.score !== '-' ? <em>{segmentIntro.score}</em> : null}
                    </div>
                  ) : null}
                  {videoUrl && !isVideoPlaying && !segmentIntro ? (
                    <button type="button" className="analysis-player-center-play" onClick={toggleVideoPlayback} title="Play">
                      <FaPlay/>
                    </button>
                  ) : null}
                  {videoUrl ? (
                    <div className="analysis-player-custom-controls">
                      <div className="analysis-player-rally-timeline" onClick={seekFromProgress}>
                        <div className="analysis-player-rally-progress" style={{width: `${Math.min(100, (currentMs / durationMs) * 100)}%`}}/>
                        {rallies.map(rally => {
                          const left = Math.max(0, (Number(rally.startMs || 0) / durationMs) * 100);
                          const width = Math.max(0.8, ((Number(rally.endMs || 0) - Number(rally.startMs || 0)) / durationMs) * 100);
                          const isCurrent = timelineRally && timelineRally.id === rally.id;
                          return (
                            <button
                              type="button"
                              key={rally.id}
                              className={`analysis-player-rally-marker ${isCurrent ? 'current' : ''}`}
                              style={{left: `${left}%`, width: `${Math.min(width, 100 - left)}%`}}
                              onClick={seekTo(rally, true, 'rallies', rallies.findIndex(item => item.id === rally.id))}
                              title={`${t('ANALYSIS_RALLY_NUMBER', {number: rally.number})} | ${t('ANALYSIS_SHOTS_COUNT', {count: rally.shotCount})} | ${teamLabel(rally.winningTeam, t)}`}
                            />
                          );
                        })}
                        <div className="analysis-player-rally-playhead" style={{left: `${Math.min(100, (currentMs / durationMs) * 100)}%`}}/>
                      </div>
                      <button type="button" className="analysis-player-play-control" onClick={toggleVideoPlayback} title={isVideoPlaying ? t('ANALYSIS_PAUSE') : t('ANALYSIS_PLAY')}>
                        {isVideoPlaying ? <FaPause/> : <FaPlay/>}
                      </button>
                      <span className="analysis-player-time">{formatTime(currentMs)} / {formatTime(durationMs)}</span>
                      <button type="button" className="analysis-player-replay-slow-control" onClick={replaySegmentSlow} disabled={!selectedSegment && rallies.length === 0} title="Replay slow">
                        <FaRedo/>
                        <span>0.5x</span>
                      </button>
                      <button type="button" className="analysis-player-frame-control analysis-player-frame-prev" onClick={stepTechnicalSegment(-0.08)} disabled={!videoUrl} title={t('ANALYSIS_PREVIOUS_FRAME')}>- frame</button>
                      <div className="analysis-technical-scrub" onMouseDown={seekFromTechnicalProgress} onMouseMove={event => {
                        if (event.buttons === 1) {
                          seekFromTechnicalProgress(event);
                        }
                      }}>
                        <div className="analysis-technical-scrub-fill" style={{width: `${technicalProgress}%`}}/>
                        <span style={{left: `${technicalProgress}%`}}/>
                      </div>
                      <button type="button" className="analysis-player-frame-control analysis-player-frame-next" onClick={stepTechnicalSegment(0.08)} disabled={!videoUrl} title={t('ANALYSIS_NEXT_FRAME')}>+ frame</button>
                      <button type="button" className="analysis-player-rate-control" onClick={changePlaybackRate} title="Slow motion">{playbackRate}x</button>
                      <button type="button" className="analysis-player-skip-control analysis-player-skip-back" onClick={seekBySeconds(-5)} title={t('ANALYSIS_BACK_SECONDS', {count: 5})}>-5</button>
                      <button type="button" className="analysis-player-skip-control analysis-player-skip-forward" onClick={seekBySeconds(5)} title={t('ANALYSIS_FORWARD_SECONDS', {count: 5})}>+5</button>
                      <button type="button" className="analysis-player-normal-replay-control" onClick={replaySegment} disabled={!selectedSegment && rallies.length === 0} title="Ripeti normale">
                        <FaRedo/>
                      </button>
                      <button type="button" className="analysis-player-fullscreen-control" onClick={toggleFullscreen} title={t('ANALYSIS_FULLSCREEN')}>
                        <FaExpand/>
                      </button>
                    </div>
                  ) : null}
                </div>
                {analysisView !== 'shot-explorer' && analysisView !== 'home' ? (
                <div className="analysis-viewer-controls">
                  <div className="analysis-rally-stepper">
                    <Button className="analysis-control-btn icon" onClick={seekRallyByDelta(-1)} disabled={rallies.length === 0} title={t('ANALYSIS_PREVIOUS_RALLY')}>
                      <FaAngleLeft/>
                    </Button>
                    <div className="analysis-rally-label">{t('ANALYSIS_RALLY_NUMBER', {number: selectedRallyNumber})}</div>
                    <Button className="analysis-control-btn icon" onClick={seekRallyByDelta(1)} disabled={rallies.length === 0} title={t('ANALYSIS_NEXT_RALLY')}>
                      <FaAngleRight/>
                    </Button>
                  </div>
                  <div className="analysis-rally-actions">
                    <Button className="analysis-control-btn icon muted" disabled title={t('ANALYSIS_CROP')}>
                      <FaCropAlt/>
                    </Button>
                    <Button className="analysis-control-btn icon muted" disabled title={t('DOWNLOAD')}>
                      <FaDownload/>
                    </Button>
                    <span className="analysis-toggle-switch" aria-hidden="true"><span/></span>
                    <Button className="analysis-control-btn icon" onClick={replaySegmentSlow} disabled={!selectedSegment && rallies.length === 0} title="Replay slow">
                      <FaRedo/>
                    </Button>
                  </div>
                  {selectedSegment ? (
                    <div className="analysis-current-segment">
                      <strong>{selectedSegment.displayTitle || translateText(selectedSegment.title || `Rally ${selectedSegment.number || Number(selectedSegment.rallyIndex) + 1 || ''}`, t)}</strong>
                      <span>{formatTime(selectedSegment.startMs)} - {formatTime(selectedSegment.endMs)}{activeRally ? ` | ${t('ANALYSIS_SHOTS_COUNT', {count: selectedRallyShots.length || activeRally.shotCount})}` : ''}</span>
                    </div>
                  ) : null}
                  {selectedSegment ? (
                    <form className="analysis-score-editor" onSubmit={saveSegmentScore}>
                      <label>
                        {t('ANALYSIS_TEAM_NUMBER', {number: 1})}
                        <input type="text" inputMode="numeric" value={scoreDraft.team1} onChange={changeScore('team1')} placeholder="0"/>
                      </label>
                      <span>-</span>
                      <label>
                        {t('ANALYSIS_TEAM_NUMBER', {number: 2})}
                        <input type="text" inputMode="numeric" value={scoreDraft.team2} onChange={changeScore('team2')} placeholder="0"/>
                      </label>
                      <Button className="analysis-score-save" type="submit" disabled={savingScore || !scoreHasChanges}>
                        {savingScore ? t('SAVING') : t('ANALYSIS_SAVE_SCORE')}
                      </Button>
                      {selectedSegment.manualScore ? (
                        <Button className="analysis-score-reset" onClick={restoreProviderScore} disabled={savingScore}>
                          {t('ANALYSIS_USE_PB_VISION')}
                        </Button>
                      ) : null}
                      {scoreSaveError ? <strong>{scoreSaveError}</strong> : null}
                    </form>
                  ) : null}
                  <div className="analysis-playback-modes">
                    <Button className={`analysis-mode-btn ${playbackMode === 'full' ? 'active' : ''}`} onClick={changePlaybackMode('full')}>
                      {t('ANALYSIS_FULL_MATCH')}
                    </Button>
                    <Button className={`analysis-mode-btn ${playbackMode === 'rallies' ? 'active' : ''}`} onClick={changePlaybackMode('rallies')} disabled={rallies.length === 0}>
                      {t('ANALYSIS_RALLIES_ONLY')}
                    </Button>
                    <Button className={`analysis-mode-btn ${playbackMode === 'highlights' ? 'active' : ''}`} onClick={changePlaybackMode('highlights')} disabled={orderedHighlights.length === 0}>
                      {t('ANALYSIS_HIGHLIGHTS_ONLY')}
                    </Button>
                  </div>
                </div>
                ) : null}
                <div className={`analysis-highlights-strip ${isShotFilterPanelOpen ? 'filters-open' : ''} ${downloadSelectionMode ? 'download-selecting' : ''}`}>
                  <div className="analysis-side-header">
                    <div className="analysis-side-title-block">
                      <span>{t('ANALYSIS_NOW_PLAYING')}</span>
                      <h3>{sidePlaylistTitle}</h3>
                      <p>{sidePlaylistMeta}</p>
                    </div>
                    <div className="analysis-side-mode-tabs">
                      <button type="button" className={activePlaylistMode === 'full' ? 'active' : ''} onClick={changePlaybackMode('full')}>
                        {t('ANALYSIS_FULL')}
                      </button>
                      <button type="button" className={activePlaylistMode === 'rallies' ? 'active' : ''} onClick={changePlaybackMode('rallies')} disabled={rallies.length === 0}>
                        {t('ANALYSIS_RALLIES')}
                      </button>
                      <button type="button" className={activePlaylistMode === 'highlights' ? 'active' : ''} onClick={changePlaybackMode('highlights')} disabled={orderedHighlights.length === 0}>
                        AI
                      </button>
                      <button type="button" className={activePlaylistMode === 'shots' ? 'active' : ''} onClick={openShotExplorerPlaylist} disabled={shotExplorerClipSegments.length === 0}>
                        {t('ANALYSIS_SHOTS')}
                      </button>
                    </div>
                    <div className="analysis-side-actions">
                      {hasAppliedShotFilters ? (
                        <button type="button" className="danger" onClick={disableShotFilters}>
                          {t('ANALYSIS_DISABLE_FILTERS')}
                        </button>
                      ) : (
                        <button type="button" className={isShotFilterPanelOpen ? 'active' : ''} onClick={toggleShotFilterPanel}>
                          {t('ANALYSIS_FILTER')}
                        </button>
                      )}
                      <button type="button" onClick={shareSidePlaylist} disabled={sidePlaylistShareDisabled}>
                        {t('SHARE')}
                      </button>
                      <button type="button" onClick={openDownloadSelection} disabled={sidePlaylistDownloadDisabled}>
                        {t('DOWNLOAD')}
                      </button>
                    </div>
                  </div>
                  {downloadSelectionMode ? (
                    <div className="analysis-download-panel">
                      <div>
                        <strong>{t('ANALYSIS_PREPARE_DOWNLOAD')}</strong>
                        <p>
                          {t('ANALYSIS_DOWNLOAD_HELP')}
                        </p>
                        <small>{t('ANALYSIS_ITEMS_SELECTED', {count: selectedDownloadItems.length})} | {formatTime(selectedDownloadDurationMs)}</small>
                      </div>
                      <div className="analysis-download-actions">
                        <button type="button" onClick={prepareDownload} disabled={selectedDownloadItems.length === 0 || downloadPreparing}>
                          {downloadPreparing ? t('ANALYSIS_PREPARING') : t('ANALYSIS_PREPARE_DOWNLOAD')}
                        </button>
                        <button type="button" onClick={toggleAllDownloadItems}>
                          {allDownloadItemsSelected ? t('DESELECT_ALL') : t('SELECT_ALL')}
                        </button>
                        <button type="button" onClick={cancelDownloadSelection}>{t('CANCEL_BUTTON')}</button>
                      </div>
                      {downloadError ? <div className="analysis-download-error">{downloadError}</div> : null}
                      {downloadResult ? (
                        <div className="analysis-download-result">
                          {downloadResult.downloadUrl || downloadResult.download_url ? (
                            <a href={downloadResult.downloadUrl || downloadResult.download_url} download>{t('ANALYSIS_DOWNLOAD_READY_VIDEO')}</a>
                          ) : downloadResult.downloadStatus === 'error' || downloadResult.status === 'error' ? (
                            <span>{t('ANALYSIS_DOWNLOAD_FAILED')}</span>
                          ) : (
                            <span>{t('ANALYSIS_DOWNLOAD_PROCESSING')}</span>
                          )}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  {isShotFilterPanelOpen ? (
                    <div className="analysis-side-filter-panel">
                      <div className="analysis-side-filter-actions">
                        <strong>{shotFilterDraftPlaybackScope === 'shot' ? `${shotFilterDraftClipCount} colpi` : `${shotFilterDraftResults.length} rally`}</strong>
                        <div>
                          <button type="button" className="primary" onClick={applyShotFilters}>{t('APPLY')}</button>
                          <button type="button" onClick={() => setShotFilterPanelOpen(false)}>{t('HIDE')}</button>
                          <button type="button" onClick={clearShotFilterDrafts}>{t('RESET')}</button>
                        </div>
                      </div>
                      <div className="analysis-shot-filter-section compact">
                        <div className="analysis-shot-filter-title">{t('ANALYSIS_PLAYERS')}</div>
                        <div className="analysis-shot-filter-grid compact">
                          {players.map(player => (
                            <button
                              type="button"
                              className={`analysis-shot-filter-chip ${shotFilterDraftPlayers.includes(player.id) ? 'active' : ''}`}
                              key={player.id}
                              onClick={toggleShotFilterDraftPlayer(player.id)}
                            >
                              {displayPlayerName(player.id)}
                              <small className="analysis-shot-filter-count">{draftPlayerFilterCount(player.id)}</small>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-shot-filter-section compact">
                        <div className="analysis-shot-filter-title">{t('ANALYSIS_RALLY_SEQUENCE')}</div>
                        <div className="analysis-shot-filter-grid compact">
                          {SHOT_SEQUENCE_FILTERS.map(filter => (
                            <button
                              type="button"
                              className={`analysis-shot-filter-chip ${shotFilterDraftTypes.includes(filter.id) ? 'active' : ''}`}
                              key={filter.id}
                              onClick={toggleShotFilterDraftType(filter.id)}
                            >
                              {t(filter.label)}
                              <small className="analysis-shot-filter-count">{draftTypeFilterCount(filter.id)}</small>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-shot-filter-section compact">
                        <div className="analysis-shot-filter-title">{t('ANALYSIS_SHOT_TYPES')}</div>
                        <div className="analysis-shot-filter-grid compact">
                          {SHOT_TYPE_FILTERS.map(filter => (
                            <button
                              type="button"
                              className={`analysis-shot-filter-chip ${shotFilterDraftTypes.includes(filter.id) ? 'active' : ''}`}
                              key={filter.id}
                              onClick={toggleShotFilterDraftType(filter.id)}
                            >
                              {t(filter.label)}
                              <small className="analysis-shot-filter-count">{draftTypeFilterCount(filter.id)}</small>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-shot-filter-section compact">
                        <div className="analysis-shot-filter-title">{t('ANALYSIS_ERRORS')}</div>
                        <div className="analysis-shot-filter-grid compact">
                          {SHOT_ERROR_FILTERS.map(filter => (
                            <button
                              type="button"
                              className={`analysis-shot-filter-chip ${shotFilterDraftTypes.includes(filter.id) ? 'active' : ''}`}
                              key={filter.id}
                              onClick={toggleShotFilterDraftType(filter.id)}
                            >
                              {t(filter.label)}
                              <small className="analysis-shot-filter-count">{draftTypeFilterCount(filter.id)}</small>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-shot-filter-section compact">
                        <div className="analysis-shot-filter-title">{t('ANALYSIS_SHOT_QUALITY')}</div>
                        <div className="analysis-quality-filter">
                          <label>{t('MIN')} <input type="number" min="0" max="100" value={shotFilterDraftQuality.min} onChange={changeShotFilterDraftQuality('min')}/></label>
                          <label>{t('MAX')} <input type="number" min="0" max="100" value={shotFilterDraftQuality.max} onChange={changeShotFilterDraftQuality('max')}/></label>
                        </div>
                      </div>
                      <div className="analysis-shot-filter-section compact">
                        <div className="analysis-shot-filter-title">{t('ANALYSIS_PLAYBACK')}</div>
                        <div className="analysis-shot-filter-grid compact">
                          {SHOT_EXPLORER_PLAYBACK_SCOPES.map(scope => (
                            <button
                              type="button"
                              className={`analysis-shot-filter-chip ${shotFilterDraftPlaybackScope === scope.id ? 'active' : ''}`}
                              key={scope.id}
                              onClick={event => {
                                event.preventDefault();
                                setShotFilterDraftPlaybackScope(scope.id);
                              }}
                            >
                              {t(scope.label)}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null}
                  <div className="analysis-side-list horizontal" ref={sidePlaylistRef}>
                    {analysisView === 'shot-explorer' ? (
                      <>
                        {shotExplorerPlaybackScope === 'shot' ? shotExplorerClipSegments.map((segment, index) => (
                          <button
                            type="button"
                            className={`analysis-side-item analysis-highlight-tile analysis-shot-rally-tile ${activeSegment && activeSegment.id === segment.id ? 'active' : ''}`}
                            data-analysis-active-card={activeSegment && activeSegment.id === segment.id ? 'true' : undefined}
                            key={segment.id}
                            onClick={seekTo(segment, true, 'shot-explorer', index)}
                          >
                            {renderDownloadToggle(segment, index)}
                            <span className="analysis-highlight-icon"><FaPlayCircle/></span>
                            <strong>{segment.displayTitle}</strong>
                            <small>{formatTime(segment.startMs)} - {formatTime(segment.endMs)}</small>
                            <span className="analysis-highlight-menu" onClick={shareAnalysisTarget({
                              mode: 'shot',
                              startMs: segment.startMs,
                              endMs: segment.endMs,
                              title: segment.displayTitle,
                              label: segment.displayTitle
                            })}><FaShareAlt/></span>
                          </button>
                        )) : shotExplorerResults.map(({rally, matchingShots}, index) => (
                          <button
                            type="button"
                            className={`analysis-side-item analysis-highlight-tile analysis-shot-rally-tile ${activeSegment && Number(activeSegment.id) === Number(rally.id) ? 'active' : ''}`}
                            data-analysis-active-card={activeSegment && Number(activeSegment.id) === Number(rally.id) ? 'true' : undefined}
                            key={rally.id}
                            onClick={seekTo(rally, true, 'shot-explorer', index)}
                          >
                            {renderDownloadToggle(rally, index)}
                            <span className="analysis-highlight-icon"><FaPlayCircle/></span>
                            <strong>Rally {rally.number}</strong>
                            <small>{matchingShots.length} {matchingShots.length === 1 ? 'colpo trovato' : 'colpi trovati'}</small>
                            <span className="analysis-highlight-menu" onClick={shareAnalysisTarget({
                              mode: 'rallies',
                              segment: rally.id,
                              label: `Rally ${rally.number}`
                            })}><FaShareAlt/></span>
                          </button>
                        ))}
                        {shotExplorerResults.length === 0 ? <div className="analysis-empty">{t('ANALYSIS_NO_RALLIES')}</div> : null}
                      </>
                    ) : (
                      <>
                        {activePlaylistMode === 'full' ? (
                          <button
                            type="button"
                            className="analysis-side-item analysis-highlight-tile active"
                            data-analysis-active-card="true"
                            onClick={changePlaybackMode('full')}
                          >
                            {renderDownloadToggle(downloadPlaylistSegments[0], 0)}
                            <span className="analysis-highlight-icon"><FaPlayCircle/></span>
                            <strong>{t('ANALYSIS_FULL_MATCH')}</strong>
                            <small>Include rally e tempi morti | {formatTime(durationMs)}</small>
                          </button>
                        ) : activePlaylistMode === 'rallies' ? rallies.map((rally, index) => (
                          <button
                            type="button"
                            className={`analysis-side-item analysis-highlight-tile ${timelineRally && Number(timelineRally.id) === Number(rally.id) ? 'active' : ''}`}
                            data-analysis-active-card={timelineRally && Number(timelineRally.id) === Number(rally.id) ? 'true' : undefined}
                            key={rally.id}
                            onClick={seekTo(rally, true, 'rallies', index)}
                          >
                            {renderDownloadToggle(rally, index)}
                            <span className="analysis-highlight-icon"><FaPlayCircle/></span>
                            <strong>Rally {rally.number}</strong>
                            <small>{formatTime(rally.startMs)} - {formatTime(rally.endMs)} | {statValue(rally.shotCount)} colpi</small>
                            <span className="analysis-highlight-menu" onClick={shareAnalysisTarget({
                              mode: 'rallies',
                              segment: rally.id,
                              label: `Rally ${rally.number}`
                            })}><FaShareAlt/></span>
                          </button>
                        )) : activePlaylistMode === 'shot-clips' ? shotClipSegments.map((segment, index) => (
                          <button
                            type="button"
                            className={`analysis-side-item analysis-highlight-tile analysis-shot-rally-tile ${activeSegment && activeSegment.id === segment.id ? 'active' : ''}`}
                            data-analysis-active-card={activeSegment && activeSegment.id === segment.id ? 'true' : undefined}
                            key={segment.id}
                            onClick={seekTo(segment, true, 'shot-clips', index)}
                          >
                            {renderDownloadToggle(segment, index)}
                            <span className="analysis-highlight-icon"><FaPlayCircle/></span>
                            <strong>{segment.displayTitle}</strong>
                            <small>Rally {segment.rally && segment.rally.number ? segment.rally.number : '-'} | {formatTime(segment.startMs)} - {formatTime(segment.endMs)}</small>
                            <span className="analysis-highlight-menu" onClick={shareAnalysisTarget({
                              mode: 'shot',
                              startMs: segment.startMs,
                              endMs: segment.endMs,
                              title: segment.displayTitle,
                              label: `${segment.displayTitle} - ${segment.playerName || 'Giocatore'}`
                            })}><FaShareAlt/></span>
                          </button>
                        )) : orderedHighlights.map((highlight, index) => (
                          <button
                            type="button"
                            className={`analysis-side-item analysis-highlight-tile ${activePlaylistHighlight && Number(activePlaylistHighlight.id) === Number(highlight.id) ? 'active' : ''}`}
                            data-analysis-active-card={activePlaylistHighlight && Number(activePlaylistHighlight.id) === Number(highlight.id) ? 'true' : undefined}
                            key={highlight.id}
                            onClick={seekTo(highlight, true, 'highlights', index)}
                          >
                            {renderDownloadToggle(highlight, index)}
                            <span className={index === 0 ? 'analysis-highlight-icon hot' : 'analysis-highlight-icon'}>
                              {index === 0 ? <FaFire/> : <FaPlayCircle/>}
                            </span>
                            <strong>{highlight.displayTitle}</strong>
                            <small>{formatTime(highlight.startMs)} - {formatTime(highlight.endMs)}</small>
                            <span className="analysis-highlight-menu" onClick={shareAnalysisTarget({
                              mode: 'highlights',
                              segment: highlight.id,
                              label: highlight.displayTitle
                            })}><FaShareAlt/></span>
                          </button>
                        ))}
                        {activePlaylistMode === 'rallies' && rallies.length === 0 ? <div className="analysis-empty">{t('ANALYSIS_NO_RALLIES')}</div> : null}
                        {activePlaylistMode === 'highlights' && orderedHighlights.length === 0 ? <div className="analysis-empty">{t('ANALYSIS_NO_HIGHLIGHTS')}</div> : null}
                        {activePlaylistMode === 'shot-clips' && shotClipSegments.length === 0 ? <div className="analysis-empty">{t('ANALYSIS_NO_SHOTS')}</div> : null}
                      </>
                    )}
                  </div>
                </div>
                <div className="analysis-report-tabs-wrap">
                  {analysisView === 'game-stats' ? (
                    <div className="analysis-game-stats">
                      <div className="analysis-game-hero">
                        <div>
                          <span>{t('ANALYSIS_GAME_STATS')}</span>
                          <h3>{formatSegmentScore(statsGame.game_outcome || overview.score)}</h3>
                          <p>{statValue(overview.totalRallies)} rally | {statValue(overview.totalShots)} colpi | media {statValue(statsGame.avg_shots || overview.averageShotsPerRally)} colpi/rally</p>
                        </div>
                        <div className="analysis-game-hero-metric">
                          <span>{t('ANALYSIS_LONGEST_RALLY')}</span>
                          <strong>{statValue((statsGame.longest_rally || {}).num_shots || overview.longestRallyShots)}</strong>
                        </div>
                        <div className="analysis-game-hero-metric">
                          <span>{t('ANALYSIS_KITCHEN_RALLIES')}</span>
                          <strong>{statValue(statsGame.kitchen_rallies || overview.kitchenRallies)}</strong>
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_MATCH_STORY')}</h4>
                        <p className="analysis-match-story">{matchStory}</p>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_STRENGTHS_WEAKNESSES')}</h4>
                        <div className="analysis-game-insights">
                          {gameInsights.map((insight, index) => (
                            <div className="analysis-game-insight" key={insight}>
                              <span>{index + 1}</span>
                              <strong>{insight}</strong>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_TEAM_COMPARISON')}</h4>
                        <div className="analysis-team-stat-grid">
                          {statsPlayersByTeam.map(team => (
                            <div className="analysis-team-stat-card" key={team.teamNumber}>
                              <strong>Team {team.teamNumber}</strong>
                              <dl>
                                <dt>{t('ANALYSIS_SHOTS')}</dt><dd>{statValue(team.shotCount)}</dd>
                                <dt>{t('ANALYSIS_AVERAGE_QUALITY')}</dt><dd>{qualityPercent(team.avgQuality)}</dd>
                                <dt>{t('ANALYSIS_KITCHEN_ARRIVAL')}</dt><dd>{qualityPercent(team.kitchen)}</dd>
                                <dt>{t('ANALYSIS_DISTANCE')}</dt><dd>{formatDistanceMeters(team.distance)}</dd>
                                <dt>{t('ANALYSIS_NET_OUT_ERRORS')}</dt><dd>{statPercent(team.netFault)} / {statPercent(team.outFault)}</dd>
                              </dl>
                              <div className="analysis-team-bars">
                                <label>{t('ANALYSIS_SHORT_RALLIES_WON')} <span>{statPercent(team.shortRallies)}</span></label>
                                <div><span style={{width: `${Math.min(100, Number(team.shortRallies || 0))}%`}}/></div>
                                <label>{t('ANALYSIS_MEDIUM_RALLIES_WON')} <span>{statPercent(team.mediumRallies)}</span></label>
                                <div><span style={{width: `${Math.min(100, Number(team.mediumRallies || 0))}%`}}/></div>
                                <label>{t('ANALYSIS_LONG_RALLIES_WON')} <span>{statPercent(team.longRallies)}</span></label>
                                <div><span style={{width: `${Math.min(100, Number(team.longRallies || 0))}%`}}/></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_SKILL_RATING')}</h4>
                        <div className="analysis-skill-rating-grid">
                          {playerStatCards.map(({player, skillRows, skillRating}) => (
                            <button type="button" className="analysis-skill-rating-card" key={player.id} onClick={openShotExplorerForPlayer(player.id)}>
                              {player.avatarUrl ? <img src={player.avatarUrl} alt={displayPlayerName(player.id)}/> : <span/>}
                              <div className="analysis-skill-rating-main">
                                <strong>{displayPlayerName(player.id)}</strong>
                                <small>Team {player.teamNumber} | Player {player.teamPlayerNumber}</small>
                                <div className="analysis-skill-score" style={{'--skill-score': `${duprRatingPercent(skillRating)}%`}}>
                                  <b>{formatRating(skillRating)}</b>
                                  <span>{t('ANALYSIS_DUPR_SCALE')}</span>
                                </div>
                              </div>
                              <div className="analysis-skill-bars">
                                {skillRows.map(row => (
                                  <label key={row.key}>
                                    <span>{row.label}</span>
                                    <div><i style={{width: `${duprRatingPercent(row.value)}%`}}/></div>
                                    <b>{formatRating(row.value)}</b>
                                  </label>
                                ))}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_PLAYER_PROFILES')}</h4>
                        <div className="analysis-game-player-grid">
                          {playerStatCards.map(({player, stats, shotProfile, mostUsedShot, bestShot, quickMetrics}) => (
                            <button type="button" className="analysis-game-player-card" key={player.id} onClick={openPlayerFocus(player.id)}>
                              {player.avatarUrl ? <img src={player.avatarUrl} alt={displayPlayerName(player.id)}/> : <span/>}
                              <div>
                                <strong>{displayPlayerName(player.id)}</strong>
                                <small>Team {player.teamNumber} | Player {player.teamPlayerNumber}</small>
                                <div className="analysis-player-quick-metrics">
                                  {quickMetrics.slice(0, 4).map(metric => (
                                    <div key={metric.label}>
                                      <span>{metric.label}</span>
                                      <b>{metric.value}</b>
                                    </div>
                                  ))}
                                </div>
                                <dl>
                                  <dt>{t('ANALYSIS_SHOT_QUALITY')}</dt><dd>{qualityPercent(stats.average_shot_quality)}</dd>
                                  <dt>{t('ANALYSIS_SHOTS')}</dt><dd>{statValue(stats.shot_count)}</dd>
                                  <dt>{t('ANALYSIS_VOLLEY_GROUND')}</dt><dd>{statValue(stats.volley_count)} / {statValue(stats.ground_stroke_count)}</dd>
                                  <dt>{t('ANALYSIS_DISTANCE')}</dt><dd>{formatDistanceMeters(stats.total_distance_covered)}</dd>
                                  <dt>{t('ANALYSIS_MOST_USED_SHOT')}</dt><dd>{mostUsedShot ? `${mostUsedShot.label} (${statValue(mostUsedShot.count)})` : '-'}</dd>
                                  <dt>{t('ANALYSIS_BEST_SHOT')}</dt><dd>{bestShot ? `${bestShot.label} ${qualityPercent(bestShot.quality)}` : '-'}</dd>
                                </dl>
                                <div className="analysis-player-shot-bars">
                                  {shotProfile.filter(shot => ['serves', 'returns', 'drives', 'dinks', 'drops'].includes(shot.key)).map(shot => (
                                    <label key={shot.key}>
                                      <span>{shot.label}</span>
                                      <div><i style={{width: `${Math.min(100, Number(shot.count || 0) * 8)}%`}}/></div>
                                      <b>{statValue(shot.count)}</b>
                                    </label>
                                  ))}
                                </div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <div className="analysis-section-title-row">
                          <div>
                            <h4>{t('ANALYSIS_SHOT_PERFORMANCE')}</h4>
                            <p className="analysis-section-help">{t('ANALYSIS_SHOT_PERFORMANCE_HELP')}</p>
                          </div>
                          <label className="analysis-shot-filter-select">
                            <span>{t('SHOW')}</span>
                            <select value={shotPerformanceFilter} onChange={event => setShotPerformanceFilter(event.target.value)}>
                              {shotPerformanceFilterOptions.map(option => (
                                <option key={option.id} value={option.id}>{option.label}</option>
                              ))}
                            </select>
                          </label>
                        </div>
                        <div className="analysis-shot-card-grid">
                          {shotPerformanceRows.map(row => (
                            <button type="button" className="analysis-shot-card" key={row.key} onClick={openShotExplorerForShot(row.filterId)}>
                              <strong>{row.label}</strong>
                              {selectedShotPerformanceFilter.type === 'all' ? (
                                <div className="analysis-shot-team-compare">
                                  {[0, 1].map(teamIndex => (
                                    <div key={teamIndex}>
                                      <span>Team {teamIndex + 1}</span>
                                      <b>{statValue(row.teamValues[teamIndex].count)}</b>
                                      <small>Qualita {qualityPercent(row.teamValues[teamIndex].quality)}</small>
                                      <small>Success {statPercent(row.teamValues[teamIndex].success)}</small>
                                      <small>Rally won {statPercent(row.teamValues[teamIndex].rallyWon)}</small>
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <div className="analysis-shot-scope-metrics">
                                  <div><span>{t('ANALYSIS_SHOTS')}</span><b>{statValue(row.scopeValue.count)}</b></div>
                                  <div><span>{t('ANALYSIS_QUALITY')}</span><b>{qualityPercent(row.scopeValue.quality)}</b></div>
                                  <div><span>{t('ANALYSIS_SUCCESS')}</span><b>{statPercent(row.scopeValue.success)}</b></div>
                                  <div><span>{t('ANALYSIS_RALLIES_WON')}</span><b>{statPercent(row.scopeValue.rallyWon)}</b></div>
                                  <div><span>{t('ANALYSIS_NET_OUT_ERRORS')}</span><b>{statPercent(row.scopeValue.netFault)} / {statPercent(row.scopeValue.outFault)}</b></div>
                                </div>
                              )}
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_WHERE_PLAYED')}</h4>
                        <p className="analysis-section-help">{t('ANALYSIS_WHERE_PLAYED_HELP')}</p>
                        <div className="analysis-zone-legend">
                          <span><i/> {t('TEAM_1')}</span>
                          <span><b/> {t('TEAM_2')}</span>
                        </div>
                        <div className="analysis-court-map">
                          {mainCourtZones.map(zone => (
                            <div
                              className={`analysis-court-map-zone zone-${zone.key}`}
                              key={zone.key}
                              style={{opacity: 0.35 + (Number(zone.totalCount || 0) / maxZoneCount) * 0.65}}
                            >
                              <strong>{zone.label}</strong>
                              <span>{statValue(zone.totalCount)} colpi</span>
                              <small>{statPercent(zone.avgSuccess)} success</small>
                              <em>{readableZoneInsight(zone)}</em>
                              <div>
                                <i style={{width: `${Math.min(100, Number(zone.teamValues[0].count || 0) / maxZoneCount * 100)}%`}}/>
                                <b style={{width: `${Math.min(100, Number(zone.teamValues[1].count || 0) / maxZoneCount * 100)}%`}}/>
                              </div>
                            </div>
                          ))}
                        </div>
                        <div className="analysis-court-side-list">
                          {sideCourtZones.map(zone => (
                            <div className="analysis-court-side-row" key={zone.key}>
                              <strong>{zone.label}</strong>
                              <p>{zone.description}</p>
                              <span>{statValue(zone.totalCount)} colpi</span>
                              <small>{statPercent(zone.avgSuccess)} success</small>
                              <em>{readableZoneInsight(zone)}</em>
                              <div>
                                <i style={{width: `${Math.min(100, Number(zone.teamValues[0].count || 0) / maxZoneCount * 100)}%`}}/>
                                <b style={{width: `${Math.min(100, Number(zone.teamValues[1].count || 0) / maxZoneCount * 100)}%`}}/>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_TRAINING_TIPS')}</h4>
                        <p className="analysis-section-help">{t('ANALYSIS_TRAINING_TIPS_HELP')}</p>
                        <div className="analysis-game-coach-list">
                          {trainingTips.slice(0, 6).map(tip => (
                            <div className="analysis-game-coach-card" key={tip.key}>
                              <strong>{tip.playerName}</strong>
                              <span>{tip.label}</span>
                              <p>{tip.message}</p>
                            </div>
                          ))}
                          {trainingTips.length === 0 ? <div className="analysis-empty">{t('ANALYSIS_NO_TIPS')}</div> : null}
                        </div>
                      </div>
                    </div>
                  ) : analysisView === 'team-stats' ? (
                    <div className="analysis-team-stats">
                      <div className="analysis-game-hero">
                        <div>
                          <span>{t('ANALYSIS_TEAM_STATS')}</span>
                          <h3>{formatSegmentScore(statsGame.game_outcome || overview.score)}</h3>
                          <p>{t('ANALYSIS_TEAM_STATS_DESCRIPTION')}</p>
                        </div>
                        <div className="analysis-game-hero-metric">
                          <span>{t('ANALYSIS_CLASSIFIED_RALLIES')}</span>
                          <strong>{statValue(teamFinishRows.reduce((sum, row) => sum + row.total, 0))}</strong>
                        </div>
                        <div className="analysis-game-hero-metric">
                          <span>{t('ANALYSIS_TOTAL_SHOTS')}</span>
                          <strong>{statValue(overview.totalShots)}</strong>
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_TEAM_SUMMARY')}</h4>
                        <div className="analysis-team-stat-grid">
                          {statsPlayersByTeam.map(team => {
                            const teamGroup = playersByTeam.find(group => Number(group.teamNumber) === Number(team.teamNumber));
                            const finish = teamFinishRows.find(row => Number(row.teamNumber) === Number(team.teamNumber)) || {};
                            return (
                              <div className="analysis-team-stat-card" key={team.teamNumber}>
                                <strong>Team {team.teamNumber}</strong>
                                <p className="analysis-team-players">
                                  {teamGroup ? teamGroup.players.map(player => displayPlayerName(player.id)).join(' / ') : '-'}
                                </p>
                                <dl>
                                  <dt>{t('ANALYSIS_WINNERS')}</dt><dd>{statValue(finish.winners)}</dd>
                                  <dt>{t('ANALYSIS_FINAL_ERRORS')}</dt><dd>{statValue(finish.errors)}</dd>
                                  <dt>{t('ANALYSIS_SHOTS')}</dt><dd>{statValue(team.shotCount)}</dd>
                                  <dt>{t('ANALYSIS_SHOT_SHARE')}</dt><dd>{statPercent(team.shotShare)}</dd>
                                  <dt>{t('ANALYSIS_AVERAGE_QUALITY')}</dt><dd>{qualityPercent(team.avgQuality)}</dd>
                                  <dt>{t('ANALYSIS_KITCHEN_ARRIVAL')}</dt><dd>{qualityPercent(team.kitchen)}</dd>
                                  <dt>{t('ANALYSIS_DISTANCE')}</dt><dd>{formatDistanceMeters(team.distance)}</dd>
                                  <dt>{t('ANALYSIS_NET_OUT_ERRORS')}</dt><dd>{statPercent(team.netFault)} / {statPercent(team.outFault)}</dd>
                                </dl>
                                <div className="analysis-team-bars">
                                  <label>{t('ANALYSIS_SHORT_RALLIES_WON')} <span>{statPercent(team.shortRallies)}</span></label>
                                  <div><span style={{width: `${Math.min(100, Number(team.shortRallies || 0))}%`}}/></div>
                                  <label>{t('ANALYSIS_MEDIUM_RALLIES_WON')} <span>{statPercent(team.mediumRallies)}</span></label>
                                  <div><span style={{width: `${Math.min(100, Number(team.mediumRallies || 0))}%`}}/></div>
                                  <label>{t('ANALYSIS_LONG_RALLIES_WON')} <span>{statPercent(team.longRallies)}</span></label>
                                  <div><span style={{width: `${Math.min(100, Number(team.longRallies || 0))}%`}}/></div>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_RALLY_ENDINGS')}</h4>
                        <p className="analysis-section-help">{t('ANALYSIS_RALLY_ENDINGS_HELP')}</p>
                        <div className="analysis-team-finish-grid">
                          {teamFinishRows.map(team => (
                            <div className="analysis-team-finish-card" key={team.teamNumber}>
                              <strong>Team {team.teamNumber}</strong>
                              <div className="analysis-team-finish-bar">
                                <i style={{width: `${team.total ? (team.winners / team.total) * 100 : 0}%`}}/>
                                <b style={{width: `${team.total ? (team.errors / team.total) * 100 : 0}%`}}/>
                              </div>
                              <div className="analysis-team-finish-values">
                                <span>{t('ANALYSIS_WINNERS')} <b>{statValue(team.winners)}</b></span>
                                <span>{t('ANALYSIS_FINAL_ERRORS')} <b>{statValue(team.errors)}</b></span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_SHOT_PERFORMANCE')}</h4>
                        <div className="analysis-shot-card-grid">
                          {shotPerformanceRows.slice(0, 8).map(row => (
                            <button type="button" className="analysis-shot-card" key={row.key} onClick={openShotExplorerForShot(row.filterId)}>
                              <strong>{row.label}</strong>
                              <div className="analysis-shot-team-compare">
                                {[0, 1].map(teamIndex => (
                                  <div key={teamIndex}>
                                    <span>Team {teamIndex + 1}</span>
                                    <b>{statValue(row.teamValues[teamIndex].count)}</b>
                                    <small>Qualita {qualityPercent(row.teamValues[teamIndex].quality)}</small>
                                    <small>Success {statPercent(row.teamValues[teamIndex].success)}</small>
                                    <small>Rally won {statPercent(row.teamValues[teamIndex].rallyWon)}</small>
                                  </div>
                                ))}
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_COURT_ZONES_BY_TEAM')}</h4>
                        <div className="analysis-zone-legend">
                          <span><i/> {t('TEAM_1')}</span>
                          <span><b/> {t('TEAM_2')}</span>
                        </div>
                        <div className="analysis-court-side-list">
                          {courtZoneRows.map(zone => (
                            <div className="analysis-court-side-row" key={zone.key}>
                              <strong>{zone.label}</strong>
                              <p>{zone.description}</p>
                              <span>{statValue(zone.totalCount)} colpi</span>
                              <small>{statPercent(zone.avgSuccess)} success</small>
                              <div>
                                <i style={{width: `${Math.min(100, Number(zone.teamValues[0].count || 0) / maxZoneCount * 100)}%`}}/>
                                <b style={{width: `${Math.min(100, Number(zone.teamValues[1].count || 0) / maxZoneCount * 100)}%`}}/>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : analysisView === 'leaderboards' ? (
                    <div className="analysis-leaderboards">
                      <div className="analysis-leaderboards-hero">
                        <span>{t('ANALYSIS_OVERALL_PERFORMANCE')}</span>
                        <h3>{t('ANALYSIS_PLAYER_RANKING')}</h3>
                        <p>{t('ANALYSIS_PLAYER_RANKING_HELP')}</p>
                      </div>
                      <div className="analysis-leaderboard-list">
                        {leaderboardRows.map((row, index) => (
                          <div className="analysis-leaderboard-card" key={row.player.id}>
                            <button type="button" className="analysis-leaderboard-player" onClick={openPlayerFocus(row.player.id)}>
                              <strong>#{index + 1}</strong>
                              {row.player.avatarUrl ? <img src={row.player.avatarUrl} alt={displayPlayerName(row.player.id)}/> : <span/>}
                              <div>
                                <b>{displayPlayerName(row.player.id)}</b>
                                <small>Team {row.player.teamNumber} | Player {row.player.teamPlayerNumber}</small>
                              </div>
                              <em>{formatRating(row.skillRating)}</em>
                            </button>
                            <div className="analysis-leaderboard-metrics">
                              <div>
                                <span>{t('ANALYSIS_SHOT_QUALITY')}</span>
                                <strong>{qualityPercent(row.stats.average_shot_quality)}</strong>
                              </div>
                              <button type="button" onClick={playRallyEndClips(row.player.id, 'winner')} disabled={row.winningRallyEnds.length === 0}>
                                <span>{t('ANALYSIS_WINNERS')}</span>
                                <strong>{statValue(row.winningRallyEnds.length)}</strong>
                              </button>
                              <button type="button" onClick={playRallyEndClips(row.player.id, 'error')} disabled={row.errorRallyEnds.length === 0}>
                                <span>{t('ANALYSIS_FINAL_ERRORS')}</span>
                                <strong>{statValue(row.errorRallyEnds.length)}</strong>
                              </button>
                              <div>
                                <span>{t('ANALYSIS_KITCHEN_ARRIVAL')}</span>
                                <strong>{statPercent(row.servingArrivalPercentage)}</strong>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                      <div className="analysis-leaderboard-bottom">
                        <div className="analysis-leaderboard-panel">
                          <h4>{t('ANALYSIS_KITCHEN_WHILE_SERVING')}</h4>
                          <p className="analysis-section-help">{t('ANALYSIS_KITCHEN_WHILE_SERVING_HELP')}</p>
                          {leaderboardRows.map(row => (
                            <div className="analysis-leaderboard-bar-row" key={`kitchen-${row.player.id}`}>
                              <span>{displayPlayerName(row.player.id)}</span>
                              <div><i style={{width: `${Math.min(100, Number(row.servingArrivalPercentage || 0))}%`}}/></div>
                              <strong>{statPercent(row.servingArrivalPercentage)}</strong>
                            </div>
                          ))}
                        </div>
                        <div className="analysis-leaderboard-panel">
                          <h4>{t('ANALYSIS_SHOT_ACCURACY')}</h4>
                          <p className="analysis-section-help">{t('ANALYSIS_SHOT_ACCURACY_HELP')}</p>
                          {leaderboardRows.map(row => (
                            <div className="analysis-accuracy-row" key={`accuracy-${row.player.id}`}>
                              <span>{displayPlayerName(row.player.id)}</span>
                              <div>
                                <i style={{width: `${Math.max(0, Math.min(100, Number(row.shotAccuracy.in || 0)))}%`}}/>
                                <b style={{width: `${Math.max(0, Math.min(100, Number(row.shotAccuracy.net || 0)))}%`}}/>
                                <em style={{width: `${Math.max(0, Math.min(100, Number(row.shotAccuracy.out || 0)))}%`}}/>
                              </div>
                              <small>In {statPercent(row.shotAccuracy.in)} | Net {statPercent(row.shotAccuracy.net)} | Out {statPercent(row.shotAccuracy.out)}</small>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : analysisView === 'player-focus' && selectedPlayerFocus ? (
                    <div className="analysis-player-focus">
                      <div className="analysis-player-focus-hero">
                        {selectedPlayerFocus.player.avatarUrl ? (
                          <img src={selectedPlayerFocus.player.avatarUrl} alt={displayPlayerName(selectedPlayerFocus.player.id)}/>
                        ) : <span/>}
                        <div>
                          <small>Team {selectedPlayerFocus.player.teamNumber} | Player {selectedPlayerFocus.player.teamPlayerNumber}</small>
                          <h3>{displayPlayerName(selectedPlayerFocus.player.id)}</h3>
                          <p>{t('ANALYSIS_PLAYER_FOCUS_DESCRIPTION')}</p>
                          <Button className="btn_red_rp" onClick={openShotExplorerForPlayer(selectedPlayerFocus.player.id)}>
                            Guarda i rally del giocatore
                          </Button>
                        </div>
                      </div>
                      <div className="analysis-player-focus-grid">
                        {selectedPlayerFocus.quickMetrics.map(metric => (
                          <div className="analysis-player-focus-metric" key={metric.label}>
                            <span>{metric.label}</span>
                            <strong>{metric.value}</strong>
                          </div>
                        ))}
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_SKILL_RATING')}</h4>
                        <div className="analysis-player-focus-skill">
                          <div className="analysis-skill-score" style={{'--skill-score': `${duprRatingPercent(selectedPlayerFocus.skillRating)}%`}}>
                            <b>{formatRating(selectedPlayerFocus.skillRating)}</b>
                            <span>{t('ANALYSIS_DUPR_SCALE')}</span>
                          </div>
                          <div className="analysis-skill-bars">
                            {selectedPlayerFocus.skillRows.map(row => (
                              <label key={row.key}>
                                <span>{row.label}</span>
                                <div><i style={{width: `${duprRatingPercent(row.value)}%`}}/></div>
                                <b>{formatRating(row.value)}</b>
                              </label>
                            ))}
                          </div>
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_PLAYER_SHOT_PERFORMANCE')}</h4>
                        <div className="analysis-shot-card-grid">
                          {selectedPlayerFocus.shotProfile.filter(shot => Number(shot.count || 0) > 0).map(shot => (
                            <button type="button" className="analysis-shot-card" key={shot.key} onClick={openShotExplorerForPlayerShot(selectedPlayerFocus.player.id, shot.filterId)}>
                              <strong>{shot.label}</strong>
                              <div className="analysis-shot-scope-metrics">
                                <div><span>{t('ANALYSIS_SHOTS')}</span><b>{statValue(shot.count)}</b></div>
                                <div><span>{t('ANALYSIS_QUALITY')}</span><b>{qualityPercent(shot.quality)}</b></div>
                                <div><span>{t('ANALYSIS_SUCCESS')}</span><b>{statPercent(shot.success)}</b></div>
                                <div><span>{t('ANALYSIS_RALLIES_WON')}</span><b>{statPercent(shot.rallyWon)}</b></div>
                                <div><span>{t('ANALYSIS_SPEED')}</span><b>{shot.maxSpeed ? t('ANALYSIS_MAX_SPEED', {value: formatSpeed(shot.maxSpeed)}) : shot.avgSpeed ? t('ANALYSIS_AVG_SPEED', {value: formatSpeed(shot.avgSpeed)}) : '-'}</b></div>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_COURT_ZONES')}</h4>
                        <div className="analysis-player-focus-zones">
                          {[
                            {label: t('ANALYSIS_ZONE_BASELINE'), stats: selectedPlayerFocus.stats.near_baseline_area},
                            {label: t('ANALYSIS_ZONE_TRANSITION'), stats: selectedPlayerFocus.stats.mid_court_area},
                            {label: 'Kitchen', stats: selectedPlayerFocus.stats.kitchen_area}
                          ].map(zone => (
                            <div key={zone.label}>
                              <strong>{zone.label}</strong>
                              <span>{t('ANALYSIS_SHOTS_COUNT', {count: statValue((zone.stats || {}).count)})}</span>
                              <small>{t('ANALYSIS_SUCCESS_VALUE', {value: statPercent(shotSuccessValue(zone.stats))})}</small>
                            </div>
                          ))}
                        </div>
                      </div>
                      <div className="analysis-game-section">
                        <h4>{t('ANALYSIS_PLAYER_TIPS')}</h4>
                        <div className="analysis-game-coach-list">
                          {trainingTips.filter(tip => tip.key.indexOf(`${selectedPlayerFocus.player.id}-`) === 0).map(tip => (
                            <div className="analysis-game-coach-card" key={tip.key}>
                              <strong>{tip.label}</strong>
                              <p>{tip.message}</p>
                            </div>
                          ))}
                          {trainingTips.filter(tip => tip.key.indexOf(`${selectedPlayerFocus.player.id}-`) === 0).length === 0 ? (
                            <div className="analysis-empty">{t('ANALYSIS_NO_PLAYER_TIPS')}</div>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  ) : analysisView === 'shot-explorer' ? (
                    <div className="analysis-shot-explorer">
                      <div className="analysis-shot-explorer-header">
                        <div>
                          <span>{t('ANALYSIS_SHOT_EXPLORER')}</span>
                          <h3>{t('ANALYSIS_SHOT_EXPLORER_TITLE')}</h3>
                        </div>
                        <strong>
                          {shotExplorerPlaybackScope === 'shot'
                            ? `${shotExplorerClipSegments.length} colpi trovati`
                            : `${shotExplorerResults.length} rally trovati`}
                        </strong>
                      </div>
                      <div className="analysis-shot-filter-section">
                        <div className="analysis-shot-filter-title">{t('ANALYSIS_PLAYERS')}</div>
                        <div className="analysis-shot-filter-grid players">
                          {players.map(player => {
                            const selected = shotExplorerPlayers.includes(player.id);
                            return (
                              <button
                                type="button"
                                className={`analysis-shot-filter-player ${selected ? 'active' : ''}`}
                                key={player.id}
                                onClick={toggleShotExplorerPlayer(player.id)}
                              >
                                {player.avatarUrl ? <img src={player.avatarUrl} alt={displayPlayerName(player.id)}/> : <span/>}
                                <strong>{displayPlayerName(player.id)}</strong>
                                <small className="analysis-shot-filter-player-count">{appliedPlayerFilterCount(player.id)} colpi</small>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      <div className="analysis-shot-filter-section">
                        <div className="analysis-shot-filter-title">{t('ANALYSIS_RALLY_SEQUENCE')}</div>
                        <div className="analysis-shot-filter-grid">
                          {SHOT_SEQUENCE_FILTERS.map(filter => {
                            const selected = shotExplorerTypes.includes(filter.id);
                            return (
                              <button
                                type="button"
                                className={`analysis-shot-filter-chip ${selected ? 'active' : ''}`}
                                key={filter.id}
                              onClick={toggleShotExplorerType(filter.id)}
                            >
                              {t(filter.label)}
                              <small className="analysis-shot-filter-count">{appliedTypeFilterCount(filter.id)}</small>
                            </button>
                          );
                        })}
                        </div>
                      </div>
                      <div className="analysis-shot-filter-section">
                        <div className="analysis-shot-filter-title">{t('ANALYSIS_SHOT_TYPES')}</div>
                        <div className="analysis-shot-filter-grid">
                          {SHOT_TYPE_FILTERS.map(filter => {
                            const selected = shotExplorerTypes.includes(filter.id);
                            return (
                              <button
                                type="button"
                                className={`analysis-shot-filter-chip ${selected ? 'active' : ''}`}
                                key={filter.id}
                              onClick={toggleShotExplorerType(filter.id)}
                            >
                              {t(filter.label)}
                              <small className="analysis-shot-filter-count">{appliedTypeFilterCount(filter.id)}</small>
                            </button>
                          );
                        })}
                        </div>
                      </div>
                      <div className="analysis-shot-filter-section">
                        <div className="analysis-shot-filter-title">{t('ANALYSIS_ERRORS')}</div>
                        <div className="analysis-shot-filter-grid">
                          {SHOT_ERROR_FILTERS.map(filter => {
                            const selected = shotExplorerTypes.includes(filter.id);
                            return (
                              <button
                                type="button"
                                className={`analysis-shot-filter-chip ${selected ? 'active' : ''}`}
                                key={filter.id}
                              onClick={toggleShotExplorerType(filter.id)}
                            >
                              {t(filter.label)}
                              <small className="analysis-shot-filter-count">{appliedTypeFilterCount(filter.id)}</small>
                            </button>
                          );
                        })}
                        </div>
                      </div>
                      <div className="analysis-shot-filter-section">
                        <div className="analysis-shot-filter-title">{t('ANALYSIS_SHOT_QUALITY')}</div>
                        <div className="analysis-quality-filter wide">
                          <label>{t('MIN')} <input type="number" min="0" max="100" value={shotExplorerQuality.min} onChange={changeShotExplorerQuality('min')}/></label>
                          <label>{t('MAX')} <input type="number" min="0" max="100" value={shotExplorerQuality.max} onChange={changeShotExplorerQuality('max')}/></label>
                          <button type="button" onClick={clearShotExplorerFilters}>{t('ANALYSIS_RESET_FILTERS')}</button>
                        </div>
                      </div>
                      <div className="analysis-shot-filter-section">
                        <div className="analysis-shot-filter-title">{t('ANALYSIS_PLAYBACK')}</div>
                        <div className="analysis-shot-filter-grid">
                          {SHOT_EXPLORER_PLAYBACK_SCOPES.map(scope => (
                            <button
                              type="button"
                              className={`analysis-shot-filter-chip ${shotExplorerPlaybackScope === scope.id ? 'active' : ''}`}
                              key={scope.id}
                              onClick={changeShotExplorerPlaybackScope(scope.id)}
                            >
                              {t(scope.label)}
                            </button>
                          ))}
                        </div>
                      </div>
                      {shotExplorerResults.length === 0 ? (
                        <div className="analysis-empty">{t('ANALYSIS_NO_RALLIES_FILTERS')}</div>
                      ) : null}
                    </div>
                  ) : (
                    <Tabs defaultActiveKey="overview" className="analysis-report-tabs">
              <Tab eventKey="overview" title={t('ANALYSIS_OVERVIEW')}>
                <div className="analysis-report-metrics">
                  {metricCards(overview, t).map(metric => (
                    <div className="analysis-report-metric" key={metric.label}>
                      <span>{metric.label}</span>
                      <strong>{formatValue(metric.value)}</strong>
                    </div>
                  ))}
                </div>
                <div className="analysis-report-section">
                  <h5>{t('ANALYSIS_PLAYERS')}</h5>
                  <div className="analysis-team-grid">
                    {playersByTeam.map(group => (
                      <div className="analysis-team-panel" key={group.teamNumber}>
                        <h6>{t('ANALYSIS_TEAM_NUMBER', {number: group.teamNumber})}</h6>
                        <div className="analysis-player-grid">
                          {group.players.map(player => (
                            <div className="analysis-player-card" key={player.id}>
                              <div className="analysis-player-header">
                                <div className="analysis-player-avatar-wrap">
                                  {player.avatarUrl ? (
                                    <img className="analysis-player-avatar" src={player.avatarUrl} alt={defaultPlayerName(player, t)}/>
                                  ) : (
                                    <div className="analysis-player-avatar fallback">T{player.teamNumber || '-'}<br/>G{player.teamPlayerNumber || '-'}</div>
                                  )}
                                </div>
                                <div className="analysis-player-title">
                                  <strong>{displayPlayerName(player.id)}</strong>
                                  <span>{t('ANALYSIS_PLAYER_NUMBER', {number: player.teamPlayerNumber || '-'})}</span>
                                </div>
                              </div>
                              <label className="analysis-player-name-field">
                                {t('ANALYSIS_PLAYER_NAME')}
                                <input
                                  type="text"
                                  maxLength="80"
                                  value={playerNames[player.id] || ''}
                                  placeholder={defaultPlayerName(player, t)}
                                  onChange={changePlayerName(player.id)}
                                />
                              </label>
                              <dl>
                                <dt>{t('ANALYSIS_SHOTS')}</dt><dd>{formatValue(player.shotCount)}</dd>
                                <dt>{t('ANALYSIS_AVERAGE_QUALITY')}</dt><dd>{formatValue(player.averageShotQuality, '%')}</dd>
                                <dt>{t('ANALYSIS_DISTANCE')}</dt><dd>{formatDistanceMeters(player.distanceCovered)}</dd>
                                <dt>{t('ANALYSIS_VOLLEY_GROUND')}</dt><dd>{formatValue(player.volleyCount)} / {formatValue(player.groundStrokeCount)}</dd>
                                <dt>{t('ANALYSIS_NET_OUT_ERRORS')}</dt><dd>{formatValue(player.netFaultPercentage, '%')} / {formatValue(player.outFaultPercentage, '%')}</dd>
                              </dl>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div className="analysis-player-save-bar">
                    {playerSaveError ? <span className="analysis-player-save-error">{playerSaveError}</span> : <span>{t('ANALYSIS_PLAYER_NAMES_HELP')}</span>}
                    <Button className="btn btn_red_rp" onClick={savePlayerNames} disabled={savingPlayers || !playerHasChanges}>
                      {savingPlayers ? t('SAVING') : t('ANALYSIS_SAVE_PLAYER_NAMES')}
                    </Button>
                  </div>
                </div>
              </Tab>
              <Tab eventKey="rallies" title={t('ANALYSIS_RALLIES')}>
                <div className="analysis-table-wrap">
                  <table className="analysis-table">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>{t('ANALYSIS_TIME')}</th>
                        <th>{t('ANALYSIS_SHOTS')}</th>
                        <th>{t('ANALYSIS_WINNER')}</th>
                        <th>{t('ANALYSIS_FINAL_SHOT')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rallies.map(rally => (
                        <tr key={rally.id}>
                          <td>{rally.number}</td>
                          <td>{formatTime(rally.startMs)} - {formatTime(rally.endMs)}</td>
                          <td>{rally.shotCount}</td>
                          <td>{teamLabel(rally.winningTeam, t)}</td>
                          <td>{translateText(rally.finalShot, t)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Tab>
              <Tab eventKey="shots" title={t('ANALYSIS_SHOTS')}>
                <div className="analysis-table-wrap">
                  <table className="analysis-table">
                    <thead>
                      <tr>
                        <th>{t('ANALYSIS_RALLY')}</th>
                        <th>{t('ANALYSIS_TIME')}</th>
                        <th>{t('ANALYSIS_PLAYER')}</th>
                        <th>{t('ANALYSIS_SHOT')}</th>
                        <th>{t('ANALYSIS_QUALITY')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shots.map(shot => (
                        <tr key={shot.id}>
                          <td>{Number(shot.rallyIndex) + 1}</td>
                          <td>{formatTime(shot.startMs)}</td>
                          <td>{displayPlayerName(shot.playerId)}</td>
                          <td>{translateText(shot.label, t)}</td>
                          <td>{formatValue(shot.quality, '%')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Tab>
              <Tab eventKey="highlights" title={t('ANALYSIS_HIGHLIGHTS')}>
                <div className="analysis-highlight-list">
                  {orderedHighlights.map((highlight, index) => (
                    <button type="button" className="analysis-highlight-item" key={highlight.id} onClick={seekTo(highlight, true, 'highlights', index)}>
                      <div>
                        <strong>{highlight.displayTitle}</strong>
                        <span>{t('ANALYSIS_RALLY_NUMBER', {number: Number(highlight.rallyIndex) + 1 || '-'})} | {formatTime(highlight.startMs)} - {formatTime(highlight.endMs)}</span>
                      </div>
                      <span>{formatValue((highlight.events || []).map(value => translateToken(value, t)).join(', '))}</span>
                    </button>
                  ))}
                  {orderedHighlights.length === 0 ? <div className="analysis-empty">{t('ANALYSIS_NO_HIGHLIGHTS')}</div> : null}
                </div>
              </Tab>
              <Tab eventKey="advice" title={t('ANALYSIS_ADVICE')}>
                <div className="analysis-advice-list">
                  {trainingTips.map(tip => (
                    <div className="analysis-advice-item" key={tip.key}>
                      <strong>{tip.label}</strong>
                      <span>{tip.message}</span>
                    </div>
                  ))}
                  {trainingTips.length === 0 ? <div className="analysis-empty">{t('ANALYSIS_NO_TIPS')}</div> : null}
                </div>
              </Tab>
                    </Tabs>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </main>
    </div>
    <PlayerVideoAnalysisSharing
      show={isAnalysisSharingModalVisible}
      request={report}
      token={token}
      target={analysisSharingTarget}
      onHide={() => setAnalysisSharingModalVisible(false)}
      onShared={updatedReport => setReport({...report, ...updatedReport})}
    />
    </>
  );
};

PlayerVideoAnalysisPage.propTypes = {
  match: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  token: PropTypes.string,
  profile: PropTypes.object
};

PlayerVideoAnalysisPage.defaultProps = {
  token: null,
  profile: null
};

export default withRouter(connect(({login: {token, profile}}) => ({token, profile}))(PlayerVideoAnalysisPage));
